import React, { useEffect, useMemo, useRef, useState } from "react";
import { useData } from "../../context/DataContext";
import { useAuth, DEFAULT_INITIAL_PASSWORD } from "../../context/AuthContext";
import {
  createEmployeeWithAccount,
  createDepartment,
  terminateEmployee,
  reactivateEmployee,
  updateEmployee,
  getEmployee,
} from "../../api";
import { getTransportLines } from "../../api/transportLines";
import {
  lookupEmployeeByCode,
  linkEmployeeAccount,
  resetEmployeePassword,
} from "../../api/employees";
import EmployeeHistoryView from "../common/EmployeeHistoryView";
import {
  fieldCompleteness,
  GENDER_OPTIONS,
} from "../../utils/profileCompleteness";
import {
  canHrAddMember,
  canManageEmployee,
  canViewEmployeeSalary,
  HR_DEPT_ID,
  isSameAccount,
} from "../../utils/hrStaffHelpers";
import {
  memberTypeToRoleSlug,
} from "../../utils/employeeAccountHelpers";
import { findEmployeeByRole, findEmployeeIdByRole } from "../../utils/roleHelpers";
import {
  assignmentUsesBranch,
  departmentUsesBranches,
  getDepartmentsForMemberType,
  isAccountsDepartment,
  isAdministrationAssignment,
  isCashierJobTitle,
  isCashierManagerJobTitle,
  isHqModeratorTeamLeaderAssignment,
  isMarketingLeadershipMember,
  isModeratorJobTitle,
  isModeratorTeamLeaderJobTitle,
  isOperationsDepartment,
  isOpsLockedMemberType,
  isOpsOnlyMemberType,
  OPS_DEPT_ID,
} from "../../utils/orgHelpers";
import { useOrganizationStructure } from "../../hooks/useOrganizationStructure";
import { filterMemberTypesForStructure } from "../../utils/orgStructures";
import {
  ADMINISTRATION_KEY,
  getBranchDisplayOptions,
  getBranchLabel,
  mapDepartments,
  mapEmployeeRow,
  resolveBackendBranchId,
  resolveBackendDepartmentId,
  resolveFrontendDepartmentId,
  toDateOnlyIso,
} from "../../utils/dataMappers";
import "./HrEmployeeManagement.css";
import "../common/EmployeeHistoryView.css";

const MEMBER_TYPES = [
  {
    id: "employee",
    label: "موظف",
    description:
      "موظف عادي — في الإدارات أو مقر التشغيل يتبع مدير القسم أو مدير الإدارة، وفي فروع التشغيل يتبع التيم ليدر أو مشرف الفرع",
  },
  {
    id: "supervisor",
    label: "مشرف / تيم ليدر",
    description:
      "مشرف فريق داخل الفرع يتبع مدير الفرع — تيم ليدر الموديريتور في المقر يتبع مديراً من التسويق",
    defaultJobTitle: "مشرف",
  },
  {
    id: "section-manager",
    label: "مدير قسم",
    description:
      "مسؤول عن قسم داخل الإدارة (مثل المخزن تحت التشغيل) — يتبع مدير الإدارة",
    defaultJobTitle: "",
  },
  {
    id: "branch-manager",
    label: "مدير فرع",
    description: "مسؤول عن فرع — يتبع مدير المنطقة اللي الفرع فيها",
    defaultJobTitle: "مدير فرع",
    defaultDepartmentId: "operations",
  },
  {
    id: "regional-manager",
    label: "مدير منطقة",
    description:
      "مسؤول عن منطقة (فروع متعددة) — فوق مديري الفروع وتحت مدير إدارة التشغيل",
    defaultJobTitle: "مدير منطقة",
    defaultDepartmentId: "operations",
  },
  {
    id: "department-manager",
    label: "مدير إدارة",
    description:
      "رتبة في الهرم — يتبع المدير العام. المسمى الوظيفي يمكن أن يكون مختلفاً",
    defaultJobTitle: "مدير إدارة",
  },
];

const EMPLOYEE_JOB_TITLES = [
  "كاشير",
  "مدير الكاشيرية",
  "مسؤول الخزنة",
  "محاسب عام",
  "محاسب",
  "سيلز",
  "خدمة عملاء",
  "تيم ليدر",
  "أخرى",
];

const MEMBER_TYPE_LABELS = Object.fromEntries(
  MEMBER_TYPES.map((t) => [t.id, t.label]),
);

const EMPTY_PENALTY = {
  type: "WARNING",
  effectiveDate: "",
  days: "",
  amount: "",
  reason: "",
};

const PENALTY_TYPES = [
  { value: "WARNING", label: "إنذار" },
  { value: "FINANCIAL_PENALTY", label: "جزاء مالي" },
];

const WORK_MODE_OPTIONS = [
  {
    value: "OFFICE",
    label: "مكتب (بصمة)",
    hint: "الحضور عبر جهاز البصمة فقط",
  },
  {
    value: "REMOTE",
    label: "عن بُعد دائم",
    hint: "الحضور من التطبيق يومياً بدون طلب",
  },
  {
    value: "HYBRID",
    label: "مختلط",
    hint: "مكتب + يمكن طلب أيام عمل من المنزل",
  },
];

const EMPTY_FORM = {
  memberType: "employee",
  name: "",
  email: "",
  code: "",
  jobTitle: "كاشير",
  customJobTitle: "",
  branchId: ADMINISTRATION_KEY,
  departmentId: "",
  lineId: "",
  joinDate: "",
  status: "active",
  salary: "",
  payFrequency: "MONTHLY",
  workMode: "OFFICE",
  manualAttendanceOnly: false,
  requiresShiftHandover: false,
  managerId: "",
  departmentManagerId: "",
  managedBranchIds: [],
  annualEntitlement: "8",
  annualUsed: "0",
  casualEntitlement: "2",
  casualUsed: "0",
  sickEntitlement: "30",
  sickUsed: "0",
  ordinaryAdvanceOutstanding: "0",
  ordinaryAdvanceInstallmentsPaid: "0",
  exceptionalAdvanceTotalAmount: "0",
  exceptionalAdvanceInstallmentsTotal: "0",
  exceptionalAdvanceInstallmentsPaid: "0",
  initialPenalties: [],
};

const EMPTY_EDIT_FORM = {
  memberType: "employee",
  name: "",
  legalFullName: "",
  email: "",
  phone: "",
  phoneSecondary: "",
  nationalId: "",
  gender: "",
  birthDate: "",
  address: "",
  departmentId: "",
  branchId: ADMINISTRATION_KEY,
  lineId: "",
  jobTitle: "",
  customJobTitle: "",
  salary: "",
  payFrequency: "MONTHLY",
  workMode: "OFFICE",
  manualAttendanceOnly: false,
  requiresShiftHandover: false,
  managerId: "",
  managedBranchIds: [],
  joinDate: "",
  annualEntitlement: "8",
  annualUsed: "0",
  casualEntitlement: "2",
  casualUsed: "0",
  sickEntitlement: "30",
  sickUsed: "0",
  ordinaryAdvanceOutstanding: "0",
  ordinaryAdvanceInstallmentsPaid: "0",
  exceptionalAdvanceTotalAmount: "0",
  exceptionalAdvanceInstallmentsTotal: "0",
  exceptionalAdvanceInstallmentsPaid: "0",
};

function roundAdvanceMoney(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/** Monthly installment = full amount ÷ installments. */
function calcExceptionalMonthlyInstallment(totalAmount, installmentsTotal) {
  const total = Number(installmentsTotal) || 0;
  const amount = Number(totalAmount) || 0;
  if (total <= 0 || amount <= 0) return 0;
  return roundAdvanceMoney(amount / total);
}

/** Remaining balance = unpaid installments × monthly installment. */
function calcExceptionalOutstanding(
  totalAmount,
  installmentsTotal,
  installmentsPaid,
) {
  const total = Number(installmentsTotal) || 0;
  const paid = Math.max(0, Number(installmentsPaid) || 0);
  const remainingInstallments = Math.max(0, total - paid);
  if (remainingInstallments <= 0) return 0;
  const monthly = calcExceptionalMonthlyInstallment(totalAmount, total);
  return roundAdvanceMoney(monthly * remainingInstallments);
}

/** Reverse full amount from stored outstanding + installment counts (for edit form). */
function deriveExceptionalTotalAmount(
  outstandingAmount,
  installmentsTotal,
  installmentsPaid,
) {
  const total = Number(installmentsTotal) || 0;
  const paid = Math.max(0, Number(installmentsPaid) || 0);
  const remainingInstallments = Math.max(0, total - paid);
  const outstanding = Number(outstandingAmount) || 0;
  if (total <= 0 || remainingInstallments <= 0 || outstanding <= 0) return 0;
  return roundAdvanceMoney((outstanding * total) / remainingInstallments);
}

function optionalEditString(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed ? trimmed : null;
}

function changedIdentityValue(formValue, originalValue) {
  const next = optionalEditString(formValue);
  const prev = optionalEditString(originalValue);
  if (next === prev) return undefined;
  return next;
}

function buildIdentityPayload(form, original = {}) {
  const payload = {
    fullName: String(form.name ?? "").trim(),
  };

  const legalFullName = changedIdentityValue(
    form.legalFullName,
    original.legalFullName,
  );
  if (legalFullName !== undefined) payload.legalFullName = legalFullName;

  const emailNext = String(form.email ?? "").trim().toLowerCase() || null;
  const emailPrev = String(original.email ?? "").trim().toLowerCase() || null;
  if (emailNext !== emailPrev) payload.email = emailNext;

  const phone = changedIdentityValue(form.phone, original.phone);
  if (phone !== undefined) payload.phone = phone;

  const phoneSecondary = changedIdentityValue(
    form.phoneSecondary,
    original.phoneSecondary,
  );
  if (phoneSecondary !== undefined) payload.phoneSecondary = phoneSecondary;

  const nationalId = changedIdentityValue(form.nationalId, original.nationalId);
  if (nationalId !== undefined) payload.nationalId = nationalId;

  const gender = changedIdentityValue(form.gender, original.gender);
  if (gender !== undefined) payload.gender = gender;

  const address = changedIdentityValue(form.address, original.address);
  if (address !== undefined) payload.address = address;

  const nextBirth = form.birthDate || null;
  const prevBirth = toDateOnlyIso(original.birthDate) || null;
  if (nextBirth && nextBirth !== prevBirth) {
    payload.birthDate = nextBirth;
  }

  return payload;
}

function getExceptionalAdvanceFormPreview(form) {
  const totalAmount = Number(form.exceptionalAdvanceTotalAmount) || 0;
  const installmentsTotal =
    Number(form.exceptionalAdvanceInstallmentsTotal) || 0;
  const installmentsPaid =
    Number(form.exceptionalAdvanceInstallmentsPaid) || 0;
  const monthly = calcExceptionalMonthlyInstallment(
    totalAmount,
    installmentsTotal,
  );
  const remainingInstallments = Math.max(
    0,
    installmentsTotal - installmentsPaid,
  );
  const outstanding = calcExceptionalOutstanding(
    totalAmount,
    installmentsTotal,
    installmentsPaid,
  );
  return { monthly, remainingInstallments, outstanding };
}

/** True when job title is a hierarchy default (not a personal/custom title). */
function isAutoHierarchyJobTitle(title, memberType) {
  const t = String(title || "").trim();
  if (!t) return true;
  const defaults = [
    "مدير إدارة",
    "مدير فرع",
    "مدير منطقة",
    "مدير قسم",
    "رئيس قسم الحسابات",
    "مشرف",
    "تيم ليدر",
  ];
  if (defaults.includes(t)) return true;
  if (
    memberType === "department-manager" &&
    (t.startsWith("مدير ") || t.includes("مدير إدارة"))
  ) {
    return true;
  }
  return false;
}

function getMemberLevel(emp) {
  if (emp.memberLevel) return emp.memberLevel;
  const roleSlugs = emp.roleSlugs || [];
  if (roleSlugs.includes("gm")) return "gm";
  if (roleSlugs.includes("owner")) return "owner";
  if (roleSlugs.includes("department_manager") || roleSlugs.includes("hr")) {
    return "department-manager";
  }
  if (roleSlugs.includes("regional_manager")) return "regional-manager";
  if (roleSlugs.includes("branch_manager")) return "branch-manager";
  if (emp.jobTitle === "مدير إدارة" || emp.jobTitle?.includes("مدير إدارة"))
    return "department-manager";
  if (emp.jobTitle === "مدير منطقة" || emp.jobTitle?.includes("مدير منطقة"))
    return "regional-manager";
  if (
    emp.jobTitle?.includes("مدير قسم") ||
    emp.jobTitle?.includes("رئيس قسم")
  ) {
    return "section-manager";
  }
  if (emp.jobTitle === "مدير فرع") return "branch-manager";
  if (emp.jobTitle === "مشرف") return "supervisor";
  if (isModeratorTeamLeaderJobTitle(emp.jobTitle)) {
    return isAdministrationAssignment(emp.branchId)
      ? "section-manager"
      : "supervisor";
  }
  if (roleSlugs.includes("manager")) {
    if (!isOperationsDepartment(emp.departmentId)) return "section-manager";
    return isAdministrationAssignment(emp.branchId)
      ? "section-manager"
      : "supervisor";
  }
  return "employee";
}

function formatManagerOption(mgr, departmentMap, branchMap) {
  if (isCashierManagerJobTitle(mgr.jobTitle)) {
    return `${mgr.name} — مدير الكاشيرية`;
  }
  const deptName = mgr.departmentId ? departmentMap[mgr.departmentId] : "";
  if (isAccountsDepartment(mgr.departmentId) && mgr.memberLevel === "department-manager") {
    return `${mgr.name} — مدير إدارة الحسابات`;
  }
  if (mgr.memberLevel === "department-manager") {
    return deptName ? `${mgr.name} — مدير ${deptName}` : `${mgr.name} (${mgr.code})`;
  }
  if (mgr.memberLevel === "regional-manager") {
    const managedNames = (mgr.managedBranches || [])
      .map((b) => b?.name)
      .filter(Boolean);
    if (managedNames.length) {
      return `${mgr.name} — مدير منطقة (${managedNames.join("، ")})`;
    }
    return deptName
      ? `${mgr.name} — مدير منطقة (${deptName})`
      : `${mgr.name} — مدير منطقة`;
  }
  if (mgr.memberLevel === "section-manager") {
    if (isAccountsDepartment(mgr.departmentId)) {
      return `${mgr.name} — رئيس قسم الحسابات`;
    }
    return deptName
      ? `${mgr.name} — مدير قسم (${deptName})`
      : `${mgr.name} — مدير قسم`;
  }
  if (isOperationsDepartment(mgr.departmentId)) {
    const branchLabel = branchMap[mgr.branchId] || "الإدارة";
    return `${mgr.name} — ${MEMBER_TYPE_LABELS[mgr.memberLevel] || mgr.jobTitle} (${branchLabel})`;
  }
  return deptName
    ? `${mgr.name} — ${mgr.jobTitle || MEMBER_TYPE_LABELS[mgr.memberLevel]} (${deptName})`
    : `${mgr.name} (${mgr.code})`;
}

function uniqueEmployeesById(employees = []) {
  const seen = new Set();
  return employees.filter((emp) => {
    if (!emp?.id || seen.has(emp.id)) return false;
    seen.add(emp.id);
    return true;
  });
}

/** Office / HQ employees can report to a section manager or the department manager. */
function officeDirectManagerOptions(sectionManagers = [], departmentManagers = []) {
  const deptList = Array.isArray(departmentManagers)
    ? departmentManagers.filter(Boolean)
    : departmentManagers
      ? [departmentManagers]
      : [];
  return uniqueEmployeesById([
    ...sectionManagers.filter(Boolean),
    ...deptList,
  ]);
}

function officeDirectManagerPlaceholder(departmentId) {
  return isAccountsDepartment(departmentId)
    ? "اختر رئيس قسم الحسابات أو مدير الإدارة"
    : "اختر مدير القسم أو مدير الإدارة";
}

function buildAllMembers(employeesByBranch, teamMembers) {
  const branchMembers = Object.entries(employeesByBranch).flatMap(
    ([branchId, employees]) =>
      employees.map((emp) => ({
        ...emp,
        branchId,
        memberLevel: getMemberLevel(emp),
      })),
  );

  return branchMembers;
}

function suggestDepartmentCode(name) {
  const latin = name
    .trim()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return latin.length >= 2 ? latin : `D${Date.now().toString().slice(-4)}`;
}

function mapStatusToBackend(status) {
  if (status === "on-leave") return "ON_LEAVE";
  if (status === "inactive") return "SUSPENDED";
  return "ACTIVE";
}

function buildHrSnapshotPayload(form) {
  const payload = {
    status: mapStatusToBackend(form.status),
    ...buildLeaveAndAdvancePayload(form),
  };

  const validPenalties = (form.initialPenalties || [])
    .filter((p) => p.type && p.reason?.trim() && p.effectiveDate)
    .map((p) => ({
      type: p.type,
      days: p.days !== "" && p.days != null ? Number(p.days) : undefined,
      amount:
        p.amount !== "" && p.amount != null ? Number(p.amount) : undefined,
      reason: p.reason.trim(),
      effectiveDate: p.effectiveDate,
    }));

  if (validPenalties.length) {
    payload.initialPenalties = validPenalties;
  }

  return payload;
}

/** Leave + advance snapshot for create/update (no status/penalties). */
function buildLeaveAndAdvancePayload(form) {
  const ordinaryOutstanding = Number(form.ordinaryAdvanceOutstanding) || 0;
  const ordinaryPaid =
    ordinaryOutstanding > 0
      ? Math.min(1, Number(form.ordinaryAdvanceInstallmentsPaid) || 0)
      : 0;
  const exceptionalTotalAmount =
    Number(form.exceptionalAdvanceTotalAmount) || 0;
  const exceptionalTotal =
    Number(form.exceptionalAdvanceInstallmentsTotal) || 0;
  const exceptionalPaid = Number(form.exceptionalAdvanceInstallmentsPaid) || 0;
  const exceptionalOutstanding = calcExceptionalOutstanding(
    exceptionalTotalAmount,
    exceptionalTotal,
    exceptionalPaid,
  );

  return {
    leaveBalance: {
      annualEntitlement: Number(form.annualEntitlement) || 8,
      annualUsed: Number(form.annualUsed) || 0,
      casualEntitlement: Number(form.casualEntitlement) || 0,
      casualUsed: Number(form.casualUsed) || 0,
      sickEntitlement: Number(form.sickEntitlement) || 30,
      sickUsed: Number(form.sickUsed) || 0,
    },
    advance: {
      ordinary: {
        outstandingAmount: ordinaryOutstanding,
        installmentsTotal: ordinaryOutstanding > 0 ? 1 : 0,
        installmentsPaid: ordinaryPaid,
      },
      exceptional: {
        outstandingAmount: exceptionalOutstanding,
        installmentsTotal: exceptionalTotal,
        installmentsPaid: exceptionalPaid,
      },
    },
  };
}

function HrEmployeeManagement({ userRole = "hr" }) {
  const {
    branches,
    departments,
    employeesByBranch,
    refreshData,
    loading: dataLoading,
  } = useData();
  const { user, isHrLoginAccount } = useAuth();
  const { selected: orgStructure } = useOrganizationStructure();

  const [apiSubmitting, setApiSubmitting] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [reactivateSubmitting, setReactivateSubmitting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState(
    DEFAULT_INITIAL_PASSWORD,
  );
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [terminateReason, setTerminateReason] = useState("");
  const [reactivateNotes, setReactivateNotes] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [completenessFilter, setCompletenessFilter] = useState("all");
  const [lineFilter, setLineFilter] = useState("all");
  const [transportLines, setTransportLines] = useState([]);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState(EMPTY_FORM);
  const [showNewDepartmentForm, setShowNewDepartmentForm] = useState(false);

  // --- Link Account state ---
  const [addModalTab, setAddModalTab] = useState("new"); // "new" | "link"
  const [linkForm, setLinkForm] = useState({
    employeeCode: "",
    email: "",
    password: DEFAULT_INITIAL_PASSWORD,
    roleSlug: "employee",
  });
  const [lookupResult, setLookupResult] = useState(null); // { found, employee }
  const [lookupLoading, setLookupLoading] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const lookupDebounce = useRef(null);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [newDepartmentCode, setNewDepartmentCode] = useState("");
  const [creatingDepartment, setCreatingDepartment] = useState(false);

  const branchMap = useMemo(
    () =>
      Object.fromEntries(
        getBranchDisplayOptions(branches).map((b) => [b.id, b.name]),
      ),
    [branches],
  );

  const departmentMap = useMemo(
    () => Object.fromEntries(departments.map((d) => [d.id, d.name])),
    [departments],
  );

  const branchFilterOptions = useMemo(
    () => getBranchDisplayOptions(branches),
    [branches],
  );

  const operationalBranches = branches;

  const allEmployees = useMemo(
    () => buildAllMembers(employeesByBranch),
    [employeesByBranch],
  );

  const departmentManagers = useMemo(
    () =>
      allEmployees.filter(
        (e) =>
          e.memberLevel === "department-manager" &&
          e.status !== "terminated",
      ),
    [allEmployees],
  );

  const sectionManagers = useMemo(
    () =>
      allEmployees.filter(
        (e) =>
          e.memberLevel === "section-manager" &&
          e.status !== "terminated" &&
          !isCashierManagerJobTitle(e.jobTitle),
      ),
    [allEmployees],
  );

  const gmEmployee = useMemo(
    () => findEmployeeByRole(allEmployees, "gm"),
    [allEmployees],
  );

  const opsDepartmentManagers = useMemo(
    () =>
      departmentManagers.filter((m) => isOperationsDepartment(m.departmentId)),
    [departmentManagers],
  );

  const isCashierForm =
    newEmployee.memberType === "employee" &&
    isCashierJobTitle(newEmployee.jobTitle);

  const usesBranchForForm = assignmentUsesBranch({
    departmentId: newEmployee.departmentId,
    memberType: newEmployee.memberType,
    jobTitle: newEmployee.jobTitle,
  });

  const isOpsHqEmployeeForm =
    isOperationsDepartment(newEmployee.departmentId) &&
    newEmployee.memberType === "employee" &&
    !isCashierForm &&
    isAdministrationAssignment(newEmployee.branchId);

  const formResolvedJobTitle =
    newEmployee.memberType === "employee" && newEmployee.jobTitle === "أخرى"
      ? newEmployee.customJobTitle
      : newEmployee.jobTitle;

  const isHqModeratorTeamLeaderForm = isHqModeratorTeamLeaderAssignment({
    jobTitle: formResolvedJobTitle,
    branchId: newEmployee.branchId,
  });

  const marketingDirectManagers = useMemo(
    () => allEmployees.filter(isMarketingLeadershipMember),
    [allEmployees],
  );

  const formBranchBackendId = useMemo(() => {
    if (
      !newEmployee.branchId ||
      newEmployee.branchId === ADMINISTRATION_KEY ||
      !usesBranchForForm
    ) {
      return null;
    }
    return resolveBackendBranchId(newEmployee.branchId, branches);
  }, [branches, newEmployee.branchId, usesBranchForForm]);

  const supervisorsInBranch = useMemo(() => {
    if (!formBranchBackendId) return [];
    return allEmployees.filter(
      (e) =>
        e.status !== "terminated" &&
        e.memberLevel === "supervisor" &&
        isOperationsDepartment(e.departmentId) &&
        (e.branchBackendId === formBranchBackendId ||
          e.branchId === newEmployee.branchId),
    );
  }, [allEmployees, formBranchBackendId, newEmployee.branchId]);

  const branchManagersInBranch = useMemo(() => {
    if (!formBranchBackendId) return [];
    return allEmployees.filter(
      (e) =>
        e.status !== "terminated" &&
        e.memberLevel === "branch-manager" &&
        isOperationsDepartment(e.departmentId) &&
        (e.branchBackendId === formBranchBackendId ||
          e.branchId === newEmployee.branchId),
    );
  }, [allEmployees, formBranchBackendId, newEmployee.branchId]);

  const regionalManagersForBranch = useMemo(() => {
    if (!formBranchBackendId) return [];
    return allEmployees.filter((e) => {
      if (
        e.status === "terminated" ||
        e.memberLevel !== "regional-manager" ||
        !isOperationsDepartment(e.departmentId)
      ) {
        return false;
      }
      const managed = e.managedBranchIds || [];
      return (
        managed.includes(formBranchBackendId) ||
        managed.includes(newEmployee.branchId)
      );
    });
  }, [allEmployees, formBranchBackendId, newEmployee.branchId]);

  const activeDepartmentManager = useMemo(() => {
    if (!newEmployee.departmentId) return null;
    return (
      departmentManagers.find(
        (mgr) => mgr.departmentId === newEmployee.departmentId,
      ) || null
    );
  }, [departmentManagers, newEmployee.departmentId]);

  const sectionManagersInDepartment = useMemo(() => {
    if (!newEmployee.departmentId) return [];
    return sectionManagers.filter(
      (mgr) => mgr.departmentId === newEmployee.departmentId,
    );
  }, [sectionManagers, newEmployee.departmentId]);

  const cashiersManager = useMemo(
    () =>
      allEmployees.find(
        (e) =>
          e.status !== "terminated" && e.jobTitle === "مدير الكاشيرية",
      ) || null,
    [allEmployees],
  );

  const directManagersForForm = useMemo(() => {
    if (isHqModeratorTeamLeaderForm) {
      return marketingDirectManagers;
    }

    if (newEmployee.memberType === "section-manager") {
      return activeDepartmentManager ? [activeDepartmentManager] : [];
    }

    if (isCashierForm) {
      return cashiersManager ? [cashiersManager] : [];
    }

    if (
      newEmployee.memberType === "employee" &&
      newEmployee.departmentId &&
      (!isOperationsDepartment(newEmployee.departmentId) ||
        isAdministrationAssignment(newEmployee.branchId))
    ) {
      return officeDirectManagerOptions(
        sectionManagersInDepartment,
        activeDepartmentManager,
      );
    }

    if (
      newEmployee.memberType === "employee" &&
      isOperationsDepartment(newEmployee.departmentId)
    ) {
      return supervisorsInBranch;
    }

    if (newEmployee.memberType === "supervisor") {
      return branchManagersInBranch;
    }

    if (newEmployee.memberType === "branch-manager") {
      return regionalManagersForBranch;
    }

    if (newEmployee.memberType === "regional-manager") {
      return opsDepartmentManagers;
    }

    return [];
  }, [
    activeDepartmentManager,
    branchManagersInBranch,
    cashiersManager,
    isCashierForm,
    isHqModeratorTeamLeaderForm,
    marketingDirectManagers,
    newEmployee.branchId,
    newEmployee.departmentId,
    newEmployee.memberType,
    opsDepartmentManagers,
    regionalManagersForBranch,
    sectionManagersInDepartment,
    supervisorsInBranch,
  ]);

  const directManagers = directManagersForForm;

  const selectedMemberType = MEMBER_TYPES.find(
    (t) => t.id === newEmployee.memberType,
  );

  // Prefer active departments in forms; inactive ones still appear in org structure.
  const formDepartments = useMemo(
    () => departments.filter((d) => d.isActive !== false),
    [departments],
  );

  const selectableDepartments = formDepartments;

  const availableMemberTypes = useMemo(
    () =>
      filterMemberTypesForStructure(orgStructure, MEMBER_TYPES, {
        departmentId: newEmployee.departmentId,
      }),
    [orgStructure, newEmployee.departmentId],
  );

  const filterMemberTypes = useMemo(() => {
    if (departmentFilter === "all") {
      return filterMemberTypesForStructure(orgStructure, MEMBER_TYPES);
    }
    return filterMemberTypesForStructure(orgStructure, MEMBER_TYPES, {
      departmentId: departmentFilter,
    });
  }, [departmentFilter, orgStructure]);

  const editMemberTypes = useMemo(
    () =>
      filterMemberTypesForStructure(orgStructure, MEMBER_TYPES, {
        departmentId: editForm.departmentId,
        currentId: editForm.memberType,
      }),
    [orgStructure, editForm.departmentId, editForm.memberType],
  );

  useEffect(() => {
    const allowed = filterMemberTypesForStructure(orgStructure, MEMBER_TYPES, {
      departmentId: newEmployee.departmentId,
    });
    if (allowed.some((type) => type.id === newEmployee.memberType)) return;
    const fallback = allowed[0]?.id || "employee";
    setNewEmployee((prev) =>
      prev.memberType === fallback ? prev : { ...prev, memberType: fallback },
    );
  }, [orgStructure, newEmployee.departmentId, newEmployee.memberType]);

  useEffect(() => {
    if (
      !orgStructure.usesBranches &&
      branches.length === 0 &&
      branchFilter !== "all"
    ) {
      setBranchFilter("all");
    }
  }, [orgStructure.usesBranches, branches.length, branchFilter]);

  useEffect(() => {
    let cancelled = false;
    getTransportLines()
      .then((rows) => {
        if (!cancelled) setTransportLines(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setTransportLines([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filterDepartments = useMemo(() => {
    const active = departments.filter((d) => d.isActive !== false);
    if (typeFilter === "all") return active;
    return getDepartmentsForMemberType(typeFilter, active);
  }, [typeFilter, departments]);

  function handleLinkCodeChange(code) {
    setLinkForm((prev) => ({ ...prev, employeeCode: code }));
    setLookupResult(null);

    if (lookupDebounce.current) clearTimeout(lookupDebounce.current);

    const trimmed = code.trim();
    if (!trimmed) return;

    lookupDebounce.current = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const result = await lookupEmployeeByCode(trimmed);
        setLookupResult(result);
      } catch {
        setLookupResult({ found: false });
      } finally {
        setLookupLoading(false);
      }
    }, 500);
  }

  async function handleLinkAccount(e) {
    e.preventDefault();

    if (!linkForm.employeeCode.trim()) {
      alert("من فضلك أدخل رقم الموظف");
      return;
    }
    if (!lookupResult?.found) {
      alert("لم يتم التحقق من رقم الموظف — تأكد من الرقم أولاً");
      return;
    }
    if (lookupResult.employee?.hasAccount) {
      alert("هذا الموظف لديه حساب بالفعل");
      return;
    }

    setLinkSubmitting(true);
    try {
      const payload = {
        employeeCode: linkForm.employeeCode.trim().toUpperCase(),
        password: linkForm.password,
        roleSlug: linkForm.roleSlug,
      };
      if (linkForm.email?.trim()) {
        payload.email = linkForm.email.trim();
      }

      const result = await linkEmployeeAccount(payload);

      const credentials = result?.loginCredentials;
      const empCode = credentials?.employeeCode || "—";
      const loginUsername = credentials?.username || "—";
      const loginEmail = credentials?.email;

      const loginDetails = loginEmail
        ? `كود الموظف: ${empCode}\nالبريد: ${loginEmail}\nكلمة المرور: ${linkForm.password}`
        : `كود الموظف: ${empCode}\nاسم المستخدم: ${loginUsername}\nكلمة المرور: ${linkForm.password}`;

      alert(
        `تم إنشاء الحساب وربطه بنجاح!\n\nبيانات الدخول:\n${loginDetails}\n\nيمكن للموظف تسجيل الدخول بكود الموظف أو اسم المستخدم.`,
      );

      await refreshData();
      setLinkForm({ employeeCode: "", email: "", password: DEFAULT_INITIAL_PASSWORD, roleSlug: "employee" });
      setLookupResult(null);
      setShowAddModal(false);
      setAddModalTab("new");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إنشاء الحساب — تحقق من البيانات أو الصلاحيات";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setLinkSubmitting(false);
    }
  }

  function canManage(employee) {
    return canManageEmployee({
      viewerRole: userRole,
      viewer: user,
      employee,
      isHrLoginAccount,
    });
  }

  const filteredEmployees = allEmployees.filter((emp) => {
    const deptLabel = emp.departmentId
      ? departmentMap[emp.departmentId] || ""
      : "";
    const matchesSearch =
      emp.name.includes(searchTerm) ||
      emp.code.includes(searchTerm) ||
      emp.jobTitle.includes(searchTerm) ||
      deptLabel.includes(searchTerm);
    const matchesBranch =
      branchFilter === "all" || emp.branchId === branchFilter;
    const matchesLine =
      lineFilter === "all" ||
      (lineFilter === "none" && !emp.lineId) ||
      emp.lineId === lineFilter;
    const matchesType =
      typeFilter === "all" || emp.memberLevel === typeFilter;
    const matchesDepartment =
      departmentFilter === "all" || emp.departmentId === departmentFilter;
    const { percent, isComplete } = fieldCompleteness(emp.profileCompleteness);
    const matchesCompleteness =
      completenessFilter === "all" ||
      (completenessFilter === "complete" && isComplete) ||
      (completenessFilter === "incomplete" && !isComplete) ||
      (completenessFilter === "low" && percent < 40);
    return (
      matchesSearch &&
      matchesBranch &&
      matchesLine &&
      matchesType &&
      matchesDepartment &&
      matchesCompleteness
    );
  });

  const activeCount = allEmployees.filter((e) => e.status === "active").length;
  const onLeaveCount = allEmployees.filter(
    (e) => e.status === "on-leave",
  ).length;
  const incompleteProfileCount = allEmployees.filter(
    (e) =>
      e.status !== "terminated" &&
      !fieldCompleteness(e.profileCompleteness).isComplete,
  ).length;

  function pickAutoDirectManagerId(memberType, departmentId, branchId, jobTitle) {
    if (jobTitle === "كاشير") {
      const cashierMgr = allEmployees.find(
        (e) =>
          e.status !== "terminated" && e.jobTitle === "مدير الكاشيرية",
      );
      if (cashierMgr) return cashierMgr.id;
    }

    if (
      isHqModeratorTeamLeaderAssignment({
        jobTitle,
        branchId,
      })
    ) {
      return marketingDirectManagers.length === 1
        ? marketingDirectManagers[0].id
        : "";
    }

    if (memberType === "section-manager") {
      return (
        departmentManagers.find((mgr) => mgr.departmentId === departmentId)
          ?.id || ""
      );
    }

    if (
      memberType === "employee" &&
      (!isOperationsDepartment(departmentId) ||
        isAdministrationAssignment(branchId))
    ) {
      const sectionMgrs = sectionManagers.filter(
        (mgr) => mgr.departmentId === departmentId,
      );
      const deptMgr = departmentManagers.find(
        (mgr) => mgr.departmentId === departmentId,
      );
      const candidates = officeDirectManagerOptions(sectionMgrs, deptMgr);
      return candidates.length === 1 ? candidates[0].id : "";
    }

    if (!isOperationsDepartment(departmentId)) {
      return "";
    }

    const branchBackendId =
      branchId && branchId !== ADMINISTRATION_KEY
        ? resolveBackendBranchId(branchId, branches)
        : null;

    if (memberType === "employee") {
      if (!branchBackendId) return "";
      const supervisors = allEmployees.filter(
        (e) =>
          e.status !== "terminated" &&
          e.memberLevel === "supervisor" &&
          isOperationsDepartment(e.departmentId) &&
          (e.branchBackendId === branchBackendId || e.branchId === branchId),
      );
      return supervisors.length === 1 ? supervisors[0].id : "";
    }

    if (memberType === "supervisor") {
      if (!branchBackendId) return "";
      const branchMgrs = allEmployees.filter(
        (e) =>
          e.status !== "terminated" &&
          e.memberLevel === "branch-manager" &&
          isOperationsDepartment(e.departmentId) &&
          (e.branchBackendId === branchBackendId || e.branchId === branchId),
      );
      return branchMgrs.length === 1 ? branchMgrs[0].id : "";
    }

    if (memberType === "branch-manager") {
      if (!branchBackendId) return "";
      const regionals = allEmployees.filter((e) => {
        if (
          e.status === "terminated" ||
          e.memberLevel !== "regional-manager" ||
          !isOperationsDepartment(e.departmentId)
        ) {
          return false;
        }
        const managed = e.managedBranchIds || [];
        return (
          managed.includes(branchBackendId) || managed.includes(branchId)
        );
      });
      return regionals.length === 1 ? regionals[0].id : "";
    }

    if (memberType === "regional-manager") {
      const opsMgrs = departmentManagers.filter((m) =>
        isOperationsDepartment(m.departmentId),
      );
      return opsMgrs.length === 1 ? opsMgrs[0].id : "";
    }

    return "";
  }

  function applyDepartmentSelection(departmentId) {
    const deptManager = departmentManagers.find(
      (mgr) => mgr.departmentId === departmentId,
    );
    const sectionMgrs = sectionManagers.filter(
      (mgr) => mgr.departmentId === departmentId,
    );
    const usesBranch = departmentUsesBranches(departmentId);

    setNewEmployee((prev) => {
      const invalidOpsRole =
        !usesBranch &&
        isOpsLockedMemberType(prev.memberType, {
          jobTitle: prev.jobTitle,
          branchId: prev.branchId,
        });
      const memberType = invalidOpsRole ? "employee" : prev.memberType;
      const cashier = isCashierJobTitle(prev.jobTitle);
      const needsBranch = assignmentUsesBranch({
        departmentId,
        memberType,
        jobTitle: prev.jobTitle,
      });
      const isSectionOrHq =
        memberType === "section-manager" ||
        (memberType === "employee" &&
          !cashier &&
          (!usesBranch || isAdministrationAssignment(prev.branchId)));
      const branchId =
        memberType === "section-manager" || !needsBranch
          ? ADMINISTRATION_KEY
          : prev.branchId && prev.branchId !== ADMINISTRATION_KEY
            ? prev.branchId
            : usesBranch
              ? prev.branchId || ADMINISTRATION_KEY
              : ADMINISTRATION_KEY;
      const autoManagerId =
        memberType === "section-manager" && deptManager
          ? deptManager.id
          : cashier
            ? pickAutoDirectManagerId(
                memberType,
                departmentId,
                branchId,
                prev.jobTitle,
              )
            : usesBranch && !isSectionOrHq
              ? pickAutoDirectManagerId(
                  memberType,
                  departmentId,
                  branchId,
                  prev.jobTitle,
                )
              : memberType === "department-manager"
                ? ""
                : memberType === "employee"
                  ? pickAutoDirectManagerId(
                      memberType,
                      departmentId,
                      branchId,
                      prev.jobTitle,
                    ) ||
                    (sectionMgrs.length === 1 ? sectionMgrs[0].id : "")
                  : prev.managerId;

      return {
        ...prev,
        departmentId,
        memberType,
        branchId,
        jobTitle:
          memberType === "section-manager" &&
          isAccountsDepartment(departmentId) &&
          (!prev.jobTitle?.trim() ||
            isAutoHierarchyJobTitle(prev.jobTitle, memberType))
            ? "رئيس قسم الحسابات"
            : prev.jobTitle,
        managerId: autoManagerId,
        departmentManagerId:
          memberType === "branch-manager" ||
          memberType === "regional-manager" ||
          memberType === "supervisor"
            ? autoManagerId
            : "",
      };
    });
  }

  function handleMemberTypeChange(memberType) {
    const typeConfig = MEMBER_TYPES.find((t) => t.id === memberType);
    const forceOperations = isOpsOnlyMemberType(memberType);

    setNewEmployee((prev) => {
      const departmentId = forceOperations
        ? OPS_DEPT_ID
        : memberType === "department-manager"
          ? ""
          : prev.departmentId;
      const branchId = ADMINISTRATION_KEY;
      const keepCustomTitle =
        memberType !== "employee" &&
        prev.jobTitle?.trim() &&
        !isAutoHierarchyJobTitle(prev.jobTitle, prev.memberType);
      const nextJobTitle =
        memberType === "employee"
          ? prev.memberType === "employee"
            ? prev.jobTitle || "كاشير"
            : "كاشير"
          : keepCustomTitle
            ? prev.jobTitle
            : memberType === "section-manager" &&
                isAccountsDepartment(departmentId)
              ? "رئيس قسم الحسابات"
              : typeConfig?.defaultJobTitle || "";
      const autoManagerId = pickAutoDirectManagerId(
        memberType,
        departmentId,
        branchId,
        memberType === "employee" ? nextJobTitle || "كاشير" : undefined,
      );

      return {
        ...prev,
        memberType,
        branchId,
        departmentId,
        jobTitle: nextJobTitle,
        customJobTitle: "",
        managerId: autoManagerId,
        departmentManagerId:
          memberType === "branch-manager" ||
          memberType === "regional-manager" ||
          memberType === "supervisor"
            ? autoManagerId
            : "",
        managedBranchIds: [],
      };
    });
    setShowNewDepartmentForm(false);
  }

  function resolveJobTitle(form, deptName) {
    if (form.memberType === "department-manager") {
      if (form.jobTitle?.trim()) return form.jobTitle.trim();
      if (isAccountsDepartment(form.departmentId)) {
        return "مدير إدارة الحسابات";
      }
      if (form.departmentId) {
        return `مدير ${deptName || departmentMap[form.departmentId] || "الإدارة"}`;
      }
      return form.jobTitle?.trim() || "مدير إدارة";
    }
    if (form.memberType === "section-manager") {
      if (form.jobTitle?.trim()) return form.jobTitle.trim();
      if (isAccountsDepartment(form.departmentId)) {
        return "رئيس قسم الحسابات";
      }
      return form.customJobTitle?.trim() || "";
    }
    if (form.memberType === "employee") {
      return form.jobTitle === "أخرى"
        ? form.customJobTitle.trim()
        : form.jobTitle;
    }
    return form.jobTitle?.trim() || selectedMemberType?.defaultJobTitle || "";
  }

  async function handleCreateDepartment() {
    const name = newDepartmentName.trim();
    const code = newDepartmentCode.trim().toUpperCase();
    if (!name) {
      alert("من فضلك أدخل اسم القسم");
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      alert("كود القسم: 2-10 أحرف/أرقام بالإنجليزية (مثل SALES)");
      return;
    }

    setCreatingDepartment(true);
    try {
      const created = await createDepartment({ name, code });
      const mapped = mapDepartments([created])[0];
      await refreshData();
      setNewEmployee((prev) => ({ ...prev, departmentId: mapped.id }));
      setNewDepartmentName("");
      setNewDepartmentCode("");
      setShowNewDepartmentForm(false);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إنشاء القسم";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setCreatingDepartment(false);
    }
  }

  function handleAddEmployee(e) {
    e.preventDefault();
    handleAddEmployeeViaApi();
  }

  async function handleAddEmployeeViaApi() {
    const isDeptManager = newEmployee.memberType === "department-manager";
    const deptName = newEmployee.departmentId
      ? departmentMap[newEmployee.departmentId]
      : "";
    const jobTitle = resolveJobTitle(newEmployee, deptName);

    if (!newEmployee.name?.trim()) {
      alert("من فضلك أدخل الاسم الكامل");
      return;
    }

    if (
      isOpsLockedMemberType(newEmployee.memberType, {
        jobTitle,
        branchId: newEmployee.branchId,
      }) &&
      !isOperationsDepartment(newEmployee.departmentId)
    ) {
      alert("مديرو الفروع والمشرفون ينتمون لإدارة التشغيل فقط");
      return;
    }

    if (newEmployee.memberType === "section-manager") {
      if (!activeDepartmentManager) {
        alert("يجب وجود مدير إدارة مسجّل لهذه الإدارة قبل إضافة مدير قسم");
        return;
      }
    }

    if (
      newEmployee.memberType === "employee" &&
      isCashierJobTitle(jobTitle)
    ) {
      if (
        !newEmployee.branchId ||
        newEmployee.branchId === ADMINISTRATION_KEY
      ) {
        alert("يجب تحديد فرع الكاشير حتى يظهر في الخطة الأسبوعية وتوجيه السلف");
        return;
      }
      if (!cashiersManager) {
        alert("يجب إنشاء حساب مدير الكاشيرية أولاً قبل إضافة كاشير");
        return;
      }
    } else if (
      newEmployee.memberType === "employee" &&
      newEmployee.departmentId &&
      (!isOperationsDepartment(newEmployee.departmentId) ||
        isAdministrationAssignment(newEmployee.branchId)) &&
      !isHqModeratorTeamLeaderAssignment({
        jobTitle,
        branchId: newEmployee.branchId,
      })
    ) {
      const officeManagers = officeDirectManagerOptions(
        sectionManagersInDepartment,
        activeDepartmentManager,
      );
      if (officeManagers.length === 0) {
        alert(
          isOperationsDepartment(newEmployee.departmentId)
            ? "يجب وجود مدير قسم أو مدير إدارة في مقر التشغيل قبل إضافة موظف للمقر"
            : "يجب وجود مدير قسم أو مدير إدارة مسجّل لهذه الإدارة قبل إضافة موظف",
        );
        return;
      }
      if (!newEmployee.managerId) {
        alert("من فضلك حدد المدير المباشر (مدير القسم أو مدير الإدارة)");
        return;
      }
    }

    if (
      newEmployee.memberType === "employee" &&
      !isCashierJobTitle(jobTitle) &&
      isOperationsDepartment(newEmployee.departmentId) &&
      !isAdministrationAssignment(newEmployee.branchId)
    ) {
      if (
        !newEmployee.branchId ||
        newEmployee.branchId === ADMINISTRATION_KEY
      ) {
        alert("الموظف في التشغيل يجب ربطه بفرع");
        return;
      }
      if (supervisorsInBranch.length === 0) {
        alert(
          "يجب وجود تيم ليدر أو مشرف فرع في هذا الفرع قبل إضافة موظف عادي",
        );
        return;
      }
      if (!newEmployee.managerId) {
        alert("من فضلك حدد المدير المباشر (تيم ليدر أو مشرف الفرع)");
        return;
      }
    }

    if (newEmployee.memberType === "supervisor") {
      const isHqModeratorTl = isHqModeratorTeamLeaderAssignment({
        jobTitle,
        branchId: newEmployee.branchId,
      });
      if (isHqModeratorTl) {
        if (marketingDirectManagers.length === 0) {
          alert(
            "يجب وجود مدير إدارة أو مدير قسم في التسويق قبل إضافة تيم ليدر الموديريتور",
          );
          return;
        }
        if (!newEmployee.managerId && !newEmployee.departmentManagerId) {
          alert("من فضلك حدد المدير المباشر من قسم التسويق");
          return;
        }
      } else {
        if (
          !newEmployee.branchId ||
          newEmployee.branchId === ADMINISTRATION_KEY
        ) {
          alert("المشرف / التيم ليدر يجب أن يكون مرتبطاً بفرع");
          return;
        }
        if (branchManagersInBranch.length === 0) {
          alert("يجب وجود مدير فرع لهذا الفرع قبل إضافة مشرف / تيم ليدر");
          return;
        }
        if (!newEmployee.managerId && !newEmployee.departmentManagerId) {
          alert("من فضلك حدد المدير المباشر (مدير الفرع)");
          return;
        }
      }
    }

    if (isDeptManager && !newEmployee.departmentId) {
      alert("من فضلك اختر الإدارة");
      return;
    }

    const gmId = isDeptManager ? findEmployeeIdByRole(allEmployees, "gm") : null;
    if (isDeptManager && !gmId) {
      alert("يجب إنشاء حساب المدير العام أولاً قبل إضافة مدير إدارة");
      return;
    }

    if (
      newEmployee.memberType === "branch-manager" &&
      (!newEmployee.branchId || newEmployee.branchId === ADMINISTRATION_KEY)
    ) {
      alert("مدير الفرع يجب أن يكون مرتبطاً بفرع");
      return;
    }

    if (
      newEmployee.memberType === "regional-manager" &&
      (!newEmployee.managedBranchIds ||
        newEmployee.managedBranchIds.length === 0)
    ) {
      alert("من فضلك اختر فرعًا واحدًا على الأقل تحت مدير المنطقة");
      return;
    }

    if (newEmployee.memberType === "branch-manager") {
      if (directManagers.length === 0) {
        alert(
          "يجب تعيين مدير منطقة لهذا الفرع أولاً قبل إضافة مدير الفرع",
        );
        return;
      }
      if (!newEmployee.departmentManagerId && !newEmployee.managerId) {
        alert("من فضلك اختر المدير المباشر (مدير المنطقة المسؤول عن الفرع)");
        return;
      }
    }

    if (newEmployee.memberType === "regional-manager") {
      if (opsDepartmentManagers.length === 0) {
        alert("يجب وجود مدير إدارة التشغيل قبل إضافة مدير منطقة");
        return;
      }
      if (!newEmployee.departmentManagerId) {
        alert("من فضلك اختر مدير إدارة التشغيل");
        return;
      }
    }

    if (!isDeptManager && !newEmployee.departmentId) {
      alert("من فضلك اختر القسم / الإدارة");
      return;
    }

    if (!jobTitle) {
      alert("من فضلك أدخل المسمى الوظيفي");
      return;
    }

    const addPermission = canHrAddMember({
      viewerRole: userRole,
      viewer: user,
      form: newEmployee,
    });
    if (!addPermission.allowed) {
      alert(addPermission.message);
      return;
    }

    const createInstallmentsTotal =
      Number(newEmployee.exceptionalAdvanceInstallmentsTotal) || 0;
    const createInstallmentsPaid =
      Number(newEmployee.exceptionalAdvanceInstallmentsPaid) || 0;
    const createTotalAmount =
      Number(newEmployee.exceptionalAdvanceTotalAmount) || 0;
    if (createInstallmentsPaid > createInstallmentsTotal) {
      alert(
        "الأقساط المدفوعة للسلفة الاستثنائية لا يمكن أن تتجاوز إجمالي الأقساط",
      );
      return;
    }
    if (createTotalAmount > 0 && createInstallmentsTotal < 2) {
      alert("السلفة الاستثنائية تتطلب قسطين على الأقل");
      return;
    }
    if (createInstallmentsTotal > 0 && createTotalAmount <= 0) {
      alert("من فضلك أدخل المبلغ الكامل للسلفة الاستثنائية");
      return;
    }

    const departmentId = resolveBackendDepartmentId(
      newEmployee.departmentId,
      departments,
    );
    if (!departmentId) {
      alert("تعذر تحديد الإدارة في النظام — تأكد من تشغيل seed للإدارات");
      return;
    }

    const email = newEmployee.email?.trim() || undefined;
    const customEmployeeCode = newEmployee.code?.trim() || undefined;

    if (
      customEmployeeCode &&
      !/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(customEmployeeCode)
    ) {
      alert(
        "كود الموظف يجب أن يبدأ بحرف أو رقم، ويحتوي فقط على حروف وأرقام وشرطة (_) أو (-)",
      );
      return;
    }

    const roleSlug = memberTypeToRoleSlug(
      newEmployee.memberType,
      newEmployee.departmentId,
      userRole,
    );

    const branchId = resolveBackendBranchId(newEmployee.branchId, branches);
    const usesBranch =
      assignmentUsesBranch({
        departmentId: newEmployee.departmentId,
        memberType: newEmployee.memberType,
        jobTitle,
      }) &&
      !(
        newEmployee.memberType === "employee" &&
        !isCashierJobTitle(jobTitle) &&
        isAdministrationAssignment(newEmployee.branchId)
      );

    const resolvedManagerId = isDeptManager
      ? gmId
      : isCashierJobTitle(jobTitle)
        ? cashiersManager?.id || newEmployee.managerId || undefined
      : newEmployee.memberType === "section-manager"
        ? activeDepartmentManager?.id
        : newEmployee.memberType === "branch-manager" ||
            newEmployee.memberType === "regional-manager" ||
            newEmployee.memberType === "supervisor"
          ? newEmployee.departmentManagerId ||
            newEmployee.managerId ||
            undefined
          : newEmployee.managerId || undefined;

    if (
      (newEmployee.memberType === "employee" ||
        newEmployee.memberType === "section-manager" ||
        newEmployee.memberType === "supervisor" ||
        newEmployee.memberType === "branch-manager" ||
        newEmployee.memberType === "regional-manager") &&
      !resolvedManagerId
    ) {
      const managerLabel = isCashierJobTitle(jobTitle)
        ? "مدير الكاشيرية"
        : newEmployee.memberType === "section-manager"
          ? "مدير الإدارة"
          : newEmployee.memberType === "supervisor"
            ? isHqModeratorTeamLeaderAssignment({
                jobTitle,
                branchId: newEmployee.branchId,
              })
              ? "مدير من التسويق"
              : "مدير الفرع"
            : newEmployee.memberType === "branch-manager"
              ? "مدير المنطقة"
              : newEmployee.memberType === "regional-manager"
                ? "مدير إدارة التشغيل"
                : !isOperationsDepartment(newEmployee.departmentId) ||
                    isAdministrationAssignment(newEmployee.branchId)
                  ? isHqModeratorTeamLeaderAssignment({
                      jobTitle,
                      branchId: newEmployee.branchId,
                    })
                    ? "مدير من التسويق"
                    : "مدير القسم أو مدير الإدارة"
                  : "التيم ليدر أو مشرف الفرع";
      alert(`من فضلك حدد المدير المباشر (${managerLabel})`);
      return;
    }

    const salaryRaw = String(newEmployee.salary ?? "").trim();
    const salaryValue = canEditSalary
      ? salaryRaw === ""
        ? null
        : Number(salaryRaw.replace(",", "."))
      : null;
    if (
      canEditSalary &&
      salaryValue !== null &&
      (!Number.isFinite(salaryValue) || salaryValue < 0)
    ) {
      alert("من فضلك أدخل راتباً صحيحاً (رقم أكبر من أو يساوي صفر)");
      return;
    }

    setApiSubmitting(true);
    try {
      const result = await createEmployeeWithAccount({
        fullName: newEmployee.name.trim(),
        ...(email ? { email } : {}),
        ...(customEmployeeCode
          ? { employeeCode: customEmployeeCode.toUpperCase() }
          : {}),
        password: DEFAULT_INITIAL_PASSWORD,
        roleSlug,
        jobTitle,
        departmentId,
        branchId: usesBranch ? branchId || undefined : undefined,
        lineId: newEmployee.lineId || undefined,
        managerId: resolvedManagerId,
        ...(newEmployee.memberType === "regional-manager"
          ? { managedBranchIds: newEmployee.managedBranchIds }
          : {}),
        hireDate: newEmployee.joinDate || new Date().toISOString().slice(0, 10),
        ...(salaryValue !== null ? { salary: salaryValue } : {}),
        ...(canEditSalary
          ? { payFrequency: newEmployee.payFrequency || "MONTHLY" }
          : {}),
        workMode: newEmployee.workMode || "OFFICE",
        manualAttendanceOnly: Boolean(newEmployee.manualAttendanceOnly),
        requiresShiftHandover:
          Boolean(newEmployee.requiresShiftHandover) ||
          isModeratorJobTitle(jobTitle),
        ...buildHrSnapshotPayload(newEmployee),
      });

      const credentials = result?.loginCredentials;
      const employeeCode =
        credentials?.employeeCode || result?.employeeCode || "—";
      const loginUsername = credentials?.username || "—";
      const loginEmail = credentials?.email;

      const loginDetails = loginEmail
        ? `كود الموظف: ${employeeCode}\nالبريد: ${loginEmail}\nكلمة المرور: ${DEFAULT_INITIAL_PASSWORD}`
        : `كود الموظف: ${employeeCode}\nاسم المستخدم: ${loginUsername}\nكلمة المرور: ${DEFAULT_INITIAL_PASSWORD}`;

      alert(
        `تم إنشاء الحساب بنجاح في قاعدة البيانات.\n\nبيانات الدخول:\n${loginDetails}\n\nيمكن للموظف تسجيل الدخول بكود الموظف أو اسم المستخدم.`,
      );

      await refreshData();

      setNewEmployee(EMPTY_FORM);
      setShowNewDepartmentForm(false);
      setNewDepartmentName("");
      setNewDepartmentCode("");
      setShowAddModal(false);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إنشاء الحساب — تحقق من البيانات أو صلاحياتك";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setApiSubmitting(false);
    }
  }

  function openDeleteModal(employee) {
    if (employee.status === "terminated") {
      alert("هذا الموظف منتهي الخدمة بالفعل");
      return;
    }

    if (!canManage(employee)) {
      alert(
        isSameAccount(employee, user)
          ? "لا يمكنك إنهاء حسابك الشخصي"
          : "إنهاء خدمة هذا الموظف — صلاحية المدير العام فقط",
      );
      return;
    }
    setSelectedEmployee(employee);
    setTerminateReason("");
    setShowDeleteModal(true);
  }

  function openReactivateModal(employee) {
    if (employee.status !== "terminated") {
      alert("هذا الموظف ليس منتهي الخدمة");
      return;
    }

    if (!canManage(employee)) {
      alert(
        isSameAccount(employee, user)
          ? "لا يمكنك إعادة تفعيل حسابك الشخصي"
          : "إعادة تفعيل هذا الموظف — صلاحية المدير العام فقط",
      );
      return;
    }
    setSelectedEmployee(employee);
    setReactivateNotes("");
    setShowReactivateModal(true);
  }

  const isAdminOrIt =
    userRole === "admin" ||
    user?.role === "admin" ||
    user?.backendRoles?.includes("admin");
  const canEditSalary = canViewEmployeeSalary(userRole, user?.backendRoles);

  function canResetPassword(employee) {
    // Password reset is restricted to Admin / IT accounts only.
    if (!isAdminOrIt) return false;
    if (!employee || employee.status === "terminated") return false;
    return true;
  }

  function openResetPasswordModal(employee) {
    if (!isAdminOrIt) {
      alert("إعادة تعيين كلمة المرور — صلاحية الأدمن / IT فقط");
      return;
    }
    if (employee.status === "terminated") {
      alert("لا يمكن إعادة تعيين كلمة مرور لموظف منتهي الخدمة");
      return;
    }
    setSelectedEmployee(employee);
    setResetPasswordValue(DEFAULT_INITIAL_PASSWORD);
    setResetPasswordResult(null);
    setShowResetPasswordModal(true);
  }

  async function confirmResetPassword() {
    if (!selectedEmployee?.id) return;

    const password = resetPasswordValue.trim();
    if (password.length < 8) {
      alert("كلمة المرور يجب أن تكون 8 أحرف على الأقل");
      return;
    }

    setResetPasswordSubmitting(true);
    try {
      await resetEmployeePassword(selectedEmployee.id, password);
      setResetPasswordResult({
        name: selectedEmployee.name,
        code: selectedEmployee.code,
        password,
      });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إعادة تعيين كلمة المرور";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setResetPasswordSubmitting(false);
    }
  }

  function closeResetPasswordModal() {
    if (resetPasswordSubmitting) return;
    setShowResetPasswordModal(false);
    setSelectedEmployee(null);
    setResetPasswordValue(DEFAULT_INITIAL_PASSWORD);
    setResetPasswordResult(null);
  }

  function buildEditFormFromEmployee(employee) {
    const memberType = getMemberLevel(employee) || "employee";
    const jobTitle = employee.jobTitle || "";
    const isPresetEmployeeTitle =
      memberType === "employee" && EMPLOYEE_JOB_TITLES.includes(jobTitle);
    const departmentId = resolveFrontendDepartmentId(
      employee.departmentId || employee.departmentBackendId,
      departments,
      employee.department?.code,
    );
    const leave = employee.leaveBalance || {};
    const advance = employee.advance || {};
    const ordinary = advance.ordinary || {};
    const exceptional = advance.exceptional || {};
    return {
      memberType,
      name: employee.name || "",
      legalFullName: employee.legalFullName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      phoneSecondary: employee.phoneSecondary || "",
      nationalId: employee.nationalId || "",
      gender: employee.gender || "",
      birthDate: toDateOnlyIso(employee.birthDate) || "",
      address: employee.address || "",
      departmentId:
        departmentId ||
        (isOpsOnlyMemberType(memberType) ? OPS_DEPT_ID : ""),
      branchId: employee.branchId || ADMINISTRATION_KEY,
      lineId: employee.lineId || "",
      jobTitle: isPresetEmployeeTitle
        ? jobTitle
        : memberType === "employee"
          ? "أخرى"
          : jobTitle,
      customJobTitle:
        memberType === "employee" && !isPresetEmployeeTitle ? jobTitle : "",
      salary:
        employee.salary === undefined || employee.salary === null
          ? ""
          : String(employee.salary),
      payFrequency:
        employee.payFrequency === "WEEKLY" ? "WEEKLY" : "MONTHLY",
      workMode: employee.workMode || "OFFICE",
      manualAttendanceOnly: Boolean(employee.manualAttendanceOnly),
      requiresShiftHandover: Boolean(employee.requiresShiftHandover),
      managerId: isCashierJobTitle(jobTitle)
        ? cashiersManager?.id || employee.managerId || ""
        : isCashierManagerJobTitle(jobTitle)
          ? employee.managerId ||
            sectionManagers.find((m) => m.departmentId === (departmentId || employee.departmentId))
              ?.id ||
            departmentManagers.find((m) => m.departmentId === (departmentId || employee.departmentId))
              ?.id ||
            ""
          : employee.managerId || "",
      managedBranchIds: Array.isArray(employee.managedBranchIds)
        ? [...employee.managedBranchIds]
        : [],
      joinDate:
        toDateOnlyIso(
          employee.joinDate || employee.hireDate || employee.hire_date,
        ) || "",
      annualEntitlement: String(leave.annualEntitlement ?? 8),
      annualUsed: String(leave.annualUsed ?? 0),
      casualEntitlement: String(leave.casualEntitlement ?? 2),
      casualUsed: String(leave.casualUsed ?? 0),
      sickEntitlement: String(leave.sickEntitlement ?? 30),
      sickUsed: String(leave.sickUsed ?? 0),
      ordinaryAdvanceOutstanding: String(ordinary.outstandingAmount ?? 0),
      ordinaryAdvanceInstallmentsPaid: String(ordinary.installmentsPaid ?? 0),
      exceptionalAdvanceTotalAmount: String(
        deriveExceptionalTotalAmount(
          exceptional.outstandingAmount,
          exceptional.installmentsTotal,
          exceptional.installmentsPaid,
        ),
      ),
      exceptionalAdvanceInstallmentsTotal: String(
        exceptional.installmentsTotal ?? 0,
      ),
      exceptionalAdvanceInstallmentsPaid: String(
        exceptional.installmentsPaid ?? 0,
      ),
    };
  }

  async function openEditModal(employee) {
    if (employee.status === "terminated") {
      alert("لا يمكن تعديل موظف منتهي الخدمة");
      return;
    }

    if (!canManage(employee)) {
      alert(
        isSameAccount(employee, user)
          ? "لا يمكنك تعديل حسابك الشخصي"
          : "تعديل هذا الموظف — صلاحية المدير العام فقط",
      );
      return;
    }

    setSelectedEmployee(employee);
    setEditForm(buildEditFormFromEmployee(employee));
    setShowEditModal(true);

    try {
      const fresh = await getEmployee(employee.id);
      if (!fresh) return;
      const mapped = mapEmployeeRow(fresh);
      const joinDate =
        toDateOnlyIso(
          mapped.joinDate ||
            fresh.hireDate ||
            employee.joinDate ||
            employee.hireDate,
        ) || "";
      const merged = {
        ...employee,
        ...mapped,
        joinDate,
        hireDate: joinDate || fresh.hireDate || employee.hireDate,
        salary:
          mapped.salary === undefined || mapped.salary === null
            ? employee.salary
            : mapped.salary,
        jobTitle: mapped.jobTitle || employee.jobTitle,
        managerId: mapped.managerId ?? employee.managerId,
        userId: mapped.userId ?? employee.userId ?? fresh.userId ?? null,
        managedBranchIds:
          mapped.managedBranchIds || employee.managedBranchIds || [],
        memberLevel: getMemberLevel({
          ...employee,
          ...mapped,
          jobTitle: mapped.jobTitle || employee.jobTitle,
          roleSlugs: mapped.roleSlugs || employee.roleSlugs,
        }),
        roleSlugs: mapped.roleSlugs || employee.roleSlugs || [],
      };
      setSelectedEmployee(merged);
      setEditForm(buildEditFormFromEmployee(merged));
    } catch {
      // Keep list data if detail fetch fails
    }
  }

  function resolveEditJobTitle() {
    if (editForm.memberType === "employee") {
      if (editForm.jobTitle === "أخرى") {
        return editForm.customJobTitle.trim();
      }
      return editForm.jobTitle.trim();
    }
    if (editForm.memberType === "department-manager") {
      return (
        editForm.jobTitle.trim() ||
        (isAccountsDepartment(editForm.departmentId)
          ? "مدير إدارة الحسابات"
          : editForm.departmentId
            ? `مدير ${departmentMap[editForm.departmentId]}`
            : "مدير إدارة")
      );
    }
    if (editForm.memberType === "section-manager") {
      if (isCashierManagerJobTitle(editForm.jobTitle)) {
        return "مدير الكاشيرية";
      }
      return (
        editForm.jobTitle.trim() ||
        (isAccountsDepartment(editForm.departmentId)
          ? "رئيس قسم الحسابات"
          : "مدير قسم")
      );
    }
    if (editForm.memberType === "branch-manager") {
      return editForm.jobTitle.trim() || "مدير فرع";
    }
    if (editForm.memberType === "regional-manager") {
      return editForm.jobTitle.trim() || "مدير منطقة";
    }
    if (editForm.memberType === "supervisor") {
      return editForm.jobTitle.trim() || "مشرف";
    }
    return editForm.jobTitle.trim();
  }

  function getEditManagerOptions() {
    const excludeId = selectedEmployee?.id;
    const inDept = (e) => e.departmentId === editForm.departmentId;
    const active = (e) => e.status !== "terminated" && e.id !== excludeId;
    const editJobTitle = resolveEditJobTitle();

    if (
      isHqModeratorTeamLeaderAssignment({
        jobTitle: editJobTitle,
        branchId: editForm.branchId,
      })
    ) {
      return marketingDirectManagers.filter(active);
    }

    if (editForm.memberType === "department-manager") {
      return gmEmployee && gmEmployee.id !== excludeId ? [gmEmployee] : [];
    }

    if (isCashierManagerJobTitle(editForm.jobTitle)) {
      const heads = sectionManagers.filter((e) => active(e) && inDept(e));
      if (heads.length) return heads;
      return departmentManagers.filter((e) => active(e) && inDept(e));
    }

    if (editForm.memberType === "section-manager") {
      return departmentManagers.filter(
        (e) => active(e) && inDept(e),
      );
    }

    if (editForm.memberType === "regional-manager") {
      return opsDepartmentManagers.filter(active);
    }

    if (editForm.memberType === "branch-manager") {
      const branchBackendId =
        editForm.branchId && editForm.branchId !== ADMINISTRATION_KEY
          ? resolveBackendBranchId(editForm.branchId, branches)
          : null;
      return allEmployees.filter((e) => {
        if (!active(e) || e.memberLevel !== "regional-manager") return false;
        if (!isOperationsDepartment(e.departmentId)) return false;
        if (!branchBackendId) return true;
        const managed = e.managedBranchIds || [];
        return (
          managed.includes(branchBackendId) || managed.includes(editForm.branchId)
        );
      });
    }

    if (editForm.memberType === "supervisor") {
      const branchBackendId =
        editForm.branchId && editForm.branchId !== ADMINISTRATION_KEY
          ? resolveBackendBranchId(editForm.branchId, branches)
          : null;
      return allEmployees.filter(
        (e) =>
          active(e) &&
          e.memberLevel === "branch-manager" &&
          isOperationsDepartment(e.departmentId) &&
          (!branchBackendId ||
            e.branchBackendId === branchBackendId ||
            e.branchId === editForm.branchId),
      );
    }

    // employee
    if (isCashierJobTitle(editForm.jobTitle)) {
      return cashiersManager && cashiersManager.id !== excludeId
        ? [cashiersManager]
        : [];
    }

    if (isOperationsDepartment(editForm.departmentId)) {
      if (isAdministrationAssignment(editForm.branchId)) {
        return officeDirectManagerOptions(
          sectionManagers.filter((e) => active(e) && inDept(e)),
          departmentManagers.filter((e) => active(e) && inDept(e)),
        );
      }
      const branchBackendId =
        editForm.branchId && editForm.branchId !== ADMINISTRATION_KEY
          ? resolveBackendBranchId(editForm.branchId, branches)
          : null;
      return allEmployees.filter(
        (e) =>
          active(e) &&
          e.memberLevel === "supervisor" &&
          isOperationsDepartment(e.departmentId) &&
          (!branchBackendId ||
            e.branchBackendId === branchBackendId ||
            e.branchId === editForm.branchId),
      );
    }

    return officeDirectManagerOptions(
      sectionManagers.filter((e) => active(e) && inDept(e)),
      departmentManagers.filter((e) => active(e) && inDept(e)),
    );
  }

  async function confirmEdit() {
    if (!selectedEmployee) return;

    const identity = buildIdentityPayload(editForm, selectedEmployee);
    if (!identity.fullName) {
      alert("من فضلك أدخل الاسم الكامل");
      return;
    }
    if (identity.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.email)) {
      alert("من فضلك أدخل بريداً إلكترونياً صحيحاً");
      return;
    }
    if (
      identity.nationalId &&
      (identity.nationalId.length < 5 || identity.nationalId.length > 30)
    ) {
      alert("الرقم القومي يجب أن يكون بين 5 و 30 حرفاً");
      return;
    }

    if (!editForm.departmentId) {
      alert("من فضلك اختر الإدارة");
      return;
    }

    const jobTitle = resolveEditJobTitle();
    if (
      isOpsLockedMemberType(editForm.memberType, {
        jobTitle,
        branchId: editForm.branchId,
      }) &&
      !isOperationsDepartment(editForm.departmentId)
    ) {
      alert("هذه الرتبة متاحة لإدارة التشغيل فقط");
      return;
    }

    if (
      editForm.memberType === "department-manager" &&
      editForm.departmentId === HR_DEPT_ID &&
      userRole !== "gm" &&
      userRole !== "admin"
    ) {
      alert("ترقية/نقل موارد بشرية — صلاحية المدير العام فقط");
      return;
    }

    const existingDeptManager = departmentManagers.find(
      (m) =>
        m.departmentId === editForm.departmentId &&
        m.id !== selectedEmployee.id,
    );
    if (
      editForm.memberType === "department-manager" &&
      existingDeptManager
    ) {
      alert(
        `هذه الإدارة لديها مدير بالفعل (${existingDeptManager.name})`,
      );
      return;
    }

    if (!jobTitle) {
      alert("من فضلك أدخل المسمى الوظيفي");
      return;
    }

    const salaryRaw = String(editForm.salary ?? "").trim();
    const salaryValue = canEditSalary
      ? salaryRaw === ""
        ? undefined
        : Number(salaryRaw.replace(",", "."))
      : undefined;
    if (
      canEditSalary &&
      salaryValue !== undefined &&
      (!Number.isFinite(salaryValue) || salaryValue < 0)
    ) {
      alert("من فضلك أدخل راتباً صحيحاً (رقم أكبر من أو يساوي صفر)");
      return;
    }

    const departmentId = resolveBackendDepartmentId(
      editForm.departmentId,
      departments,
    );
    if (!departmentId) {
      alert("تعذر تحديد الإدارة في النظام");
      return;
    }

    const roleSlug = memberTypeToRoleSlug(
      editForm.memberType,
      editForm.departmentId,
      userRole,
    );

    const usesBranch =
      assignmentUsesBranch({
        departmentId: editForm.departmentId,
        memberType: editForm.memberType,
        jobTitle,
      }) &&
      !(
        editForm.memberType === "employee" &&
        !isCashierJobTitle(jobTitle) &&
        isAdministrationAssignment(editForm.branchId)
      );

    const branchId = usesBranch
      ? resolveBackendBranchId(editForm.branchId, branches) || null
      : null;

    let managerId = editForm.managerId || null;
    if (editForm.memberType === "department-manager") {
      managerId = gmEmployee?.id || null;
    } else if (isCashierJobTitle(jobTitle)) {
      const cashierMgr = allEmployees.find(
        (e) =>
          e.status !== "terminated" && e.jobTitle === "مدير الكاشيرية",
      );
      managerId = cashierMgr?.id || managerId;
      if (!branchId) {
        alert("يجب تحديد فرع الكاشير حتى يظهر في الخطة الأسبوعية وتوجيه السلف");
        return;
      }
      if (!managerId) {
        alert("يجب إنشاء حساب مدير الكاشيرية أولاً قبل نقل/تعديل كاشير");
        return;
      }
    }

    if (
      (editForm.memberType === "employee" ||
        editForm.memberType === "section-manager" ||
        editForm.memberType === "supervisor" ||
        editForm.memberType === "branch-manager" ||
        editForm.memberType === "regional-manager") &&
      !managerId
    ) {
      alert("من فضلك حدد المدير المباشر");
      return;
    }

    if (
      editForm.memberType === "regional-manager" &&
      (!editForm.managedBranchIds || editForm.managedBranchIds.length === 0)
    ) {
      alert("من فضلك اختر فرعاً واحداً على الأقل لمدير المنطقة");
      return;
    }

    const installmentsTotal =
      Number(editForm.exceptionalAdvanceInstallmentsTotal) || 0;
    const installmentsPaid =
      Number(editForm.exceptionalAdvanceInstallmentsPaid) || 0;
    const totalAmount = Number(editForm.exceptionalAdvanceTotalAmount) || 0;
    if (installmentsPaid > installmentsTotal) {
      alert(
        "الأقساط المدفوعة للسلفة الاستثنائية لا يمكن أن تتجاوز إجمالي الأقساط",
      );
      return;
    }
    if (totalAmount > 0 && installmentsTotal < 2) {
      alert("السلفة الاستثنائية تتطلب قسطين على الأقل");
      return;
    }
    if (installmentsTotal > 0 && totalAmount <= 0) {
      alert("من فضلك أدخل المبلغ الكامل للسلفة الاستثنائية");
      return;
    }

    const hasLoginAccount = Boolean(
      selectedEmployee.userId || selectedEmployee.hasAccount,
    );
    const currentMemberType =
      selectedEmployee.memberLevel ||
      getMemberLevel(selectedEmployee) ||
      "employee";
    if (!hasLoginAccount && editForm.memberType !== currentMemberType) {
      alert(
        "لا يمكن تغيير الرتبة قبل ربط حساب دخول للموظف. اربط الحساب أولاً من «ربط حساب» ثم أعد الترقية.",
      );
      return;
    }

    const payload = {
      ...identity,
      departmentId,
      jobTitle,
      managerId,
      branchId,
      lineId: editForm.lineId || null,
      ...(hasLoginAccount ? { roleSlug } : {}),
      ...(salaryValue !== undefined ? { salary: salaryValue } : {}),
      ...(canEditSalary
        ? { payFrequency: editForm.payFrequency || "MONTHLY" }
        : {}),
      ...(editForm.joinDate ? { hireDate: editForm.joinDate } : {}),
      workMode: editForm.workMode || "OFFICE",
      manualAttendanceOnly: Boolean(editForm.manualAttendanceOnly),
      requiresShiftHandover: Boolean(editForm.requiresShiftHandover),
      ...(editForm.memberType === "regional-manager"
        ? { managedBranchIds: editForm.managedBranchIds }
        : {}),
      ...buildLeaveAndAdvancePayload(editForm),
    };

    setEditSubmitting(true);
    try {
      await updateEmployee(selectedEmployee.id, payload);
      await refreshData();
      setShowEditModal(false);
      setSelectedEmployee(null);
      setEditForm(EMPTY_EDIT_FORM);
      alert("تم تحديث بيانات الموظف بنجاح");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر تحديث بيانات الموظف";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setEditSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!selectedEmployee) return;

    const reason = terminateReason.trim();
    if (reason.length < 3) {
      alert("يرجى كتابة سبب إنهاء الخدمة (3 أحرف على الأقل)");
      return;
    }

    setDeleteSubmitting(true);
    try {
      await terminateEmployee(selectedEmployee.id, {
        notes: reason,
      });
      await refreshData();
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      setTerminateReason("");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إنهاء خدمة الموظف";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  async function confirmReactivate() {
    if (!selectedEmployee) return;

    const employeeName = selectedEmployee.name;
    setReactivateSubmitting(true);
    try {
      const notes = reactivateNotes.trim();
      await reactivateEmployee(
        selectedEmployee.id,
        notes ? { notes } : {},
      );
      await refreshData();
      setShowReactivateModal(false);
      setSelectedEmployee(null);
      setReactivateNotes("");
      alert(
        `تم إعادة تفعيل ${employeeName} بنجاح — بنفس بياناته ويمكنه تسجيل الدخول مجدداً`,
      );
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إعادة تفعيل الموظف";
      alert(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setReactivateSubmitting(false);
    }
  }

  function getMemberLevelBadge(emp) {
    const labels = {
      employee: "موظف",
      supervisor: "مشرف",
      "section-manager": "مدير قسم",
      "branch-manager": "مدير فرع",
      "regional-manager": "مدير منطقة",
      "department-manager": "مدير إدارة",
    };
    const level = emp.memberLevel;
    const deptName = emp.departmentId
      ? departmentMap[emp.departmentId]
      : null;
    let badgeText = labels[level] || level;
    if (isCashierManagerJobTitle(emp.jobTitle)) {
      badgeText = "مدير الكاشيرية";
    } else if (isAccountsDepartment(emp.departmentId) && level === "department-manager") {
      badgeText = "مدير إدارة الحسابات";
    } else if (
      isAccountsDepartment(emp.departmentId) &&
      level === "section-manager"
    ) {
      badgeText = "رئيس قسم الحسابات";
    } else if (level === "department-manager" && deptName) {
      badgeText = `مدير إدارة — ${deptName}`;
    } else if (level === "section-manager" && deptName) {
      badgeText = `مدير قسم — ${deptName}`;
    } else if (level === "employee" && deptName) {
      badgeText = `${labels.employee} — ${deptName}`;
    }
    return (
      <span className={`hr-emp-level-badge level-${level}`}>
        {badgeText}
      </span>
    );
  }

  function getStatusBadge(status) {
    if (status === "active")
      return <span className="hr-emp-status active">نشط</span>;
    if (status === "on-leave")
      return <span className="hr-emp-status on-leave">في إجازة</span>;
    if (status === "terminated")
      return <span className="hr-emp-status terminated">منتهي الخدمة</span>;
    return <span className="hr-emp-status inactive">غير نشط</span>;
  }

  if (historyEmployee) {
    return (
      <EmployeeHistoryView
        employee={historyEmployee}
        userRole={userRole}
        onBack={() => setHistoryEmployee(null)}
      />
    );
  }

  return (
    <div className="hr-employee-management" dir="rtl">
      <div className="hr-emp-header">
        <div>
          <h2 className="hr-emp-title">إدارة الأعضاء</h2>
          {isAdminOrIt && (
            <p className="hr-emp-reset-hint">
              لإعادة تعيين كلمة مرور أي حساب: من عمود الإجراءات اضغط{" "}
              <strong>إعادة تعيين كلمة المرور</strong>
            </p>
          )}
          <p className="hr-emp-subtitle">
            {dataLoading
              ? "جاري تحميل البيانات من الخادم..."
              : `${allEmployees.length} عضو | ${activeCount} نشط | ${onLeaveCount} في إجازة | ${incompleteProfileCount} ملف ناقص`}
          </p>
        </div>
        <button
          className="hr-emp-add-btn"
          onClick={() => setShowAddModal(true)}
        >
          + إضافة عضو
        </button>
      </div>

      <div className="hr-emp-filters">
        <input
          type="text"
          placeholder="بحث بالاسم، الكود، أو المسمى الوظيفي..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="hr-emp-search-input"
        />
        <select
          value={typeFilter}
          onChange={(e) => {
            const nextType = e.target.value;
            setTypeFilter(nextType);
            if (nextType === "all") return;
            const allowedDepts = getDepartmentsForMemberType(
              nextType,
              departments,
            );
            if (
              departmentFilter !== "all" &&
              !allowedDepts.some((dept) => dept.id === departmentFilter)
            ) {
              setDepartmentFilter("all");
            }
          }}
          className="hr-emp-branch-filter"
        >
          <option value="all">جميع الرتب</option>
          {filterMemberTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.label}
            </option>
          ))}
        </select>
        <select
          value={departmentFilter}
          onChange={(e) => {
            const nextDept = e.target.value;
            setDepartmentFilter(nextDept);
            if (nextDept === "all") return;
            const allowedTypes = filterMemberTypesForStructure(
              orgStructure,
              MEMBER_TYPES,
              { departmentId: nextDept },
            );
            if (
              typeFilter !== "all" &&
              !allowedTypes.some((type) => type.id === typeFilter)
            ) {
              setTypeFilter("all");
            }
            if (
              !isOperationsDepartment(nextDept) &&
              branchFilter !== "all"
            ) {
              setBranchFilter("all");
            }
          }}
          className="hr-emp-branch-filter"
        >
          <option value="all">جميع الإدارات</option>
          {filterDepartments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
        {(orgStructure.usesBranches || branches.length > 0) && (
        <select
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          className="hr-emp-branch-filter"
        >
          <option value="all">جميع الفروع (التشغيل)</option>
          {branchFilterOptions
            .filter((branch) => branch.id !== ADMINISTRATION_KEY)
            .map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
        )}
        <select
          value={completenessFilter}
          onChange={(e) => setCompletenessFilter(e.target.value)}
          className="hr-emp-branch-filter"
        >
          <option value="all">كل نسب الاكتمال</option>
          <option value="incomplete">ملف ناقص</option>
          <option value="complete">ملف مكتمل</option>
          <option value="low">أقل من 40%</option>
        </select>
        <select
          value={lineFilter}
          onChange={(e) => setLineFilter(e.target.value)}
          className="hr-emp-branch-filter"
        >
          <option value="all">جميع الخطوط</option>
          <option value="none">بدون خط</option>
          {transportLines.map((line) => (
            <option key={line.id} value={line.id}>
              {line.name}
              {line.area ? ` — ${line.area}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="hr-emp-table-card">
        <table className="hr-emp-table">
          <thead>
            <tr>
              <th>العضو</th>
              <th>الرتبة / الإدارة</th>
              <th>الفرع</th>
              <th>الخط</th>
              <th>المسمى الوظيفي</th>
              <th>تاريخ الانضمام</th>
              <th>اكتمال الملف</th>
              <th>الحالة</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp) => (
              <tr
                key={emp.code}
                className="clickable"
                onClick={() => setHistoryEmployee(emp)}
              >
                <td className="hr-emp-member-cell" data-label="العضو">
                  <div className="hr-emp-member-info">
                    <div className="hr-emp-avatar">{emp.name.charAt(0)}</div>
                    <div>
                      <div className="hr-emp-member-name-row">
                        <div className="hr-emp-member-name">{emp.name}</div>
                        {emp.status === "terminated" &&
                          getStatusBadge(emp.status)}
                      </div>
                      <div className="hr-emp-member-code">{emp.code}</div>
                    </div>
                  </div>
                </td>
                <td data-label="الرتبة / الإدارة">{getMemberLevelBadge(emp)}</td>
                <td data-label="الفرع">{getBranchLabel(emp.branchId, branchMap)}</td>
                <td data-label="الخط">{emp.lineName || "—"}</td>
                <td data-label="المسمى الوظيفي">{emp.jobTitle}</td>
                <td data-label="تاريخ الانضمام">{emp.joinDate}</td>
                <td data-label="اكتمال الملف">
                  {(() => {
                    const { percent, isComplete: complete } = fieldCompleteness(
                      emp.profileCompleteness,
                    );
                    return (
                      <span
                        className={`hr-emp-completion hr-emp-completion--${
                          complete ? "ok" : percent < 40 ? "low" : "warn"
                        }`}
                      >
                        {percent}%
                      </span>
                    );
                  })()}
                </td>
                <td data-label="الحالة">{getStatusBadge(emp.status)}</td>
                <td className="hr-emp-actions-cell" data-label="إجراءات">
                  <div
                    className="eh-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {emp.status === "terminated" ? (
                      canManage(emp) ? (
                        <button
                          type="button"
                          className="hr-emp-action-btn reactivate"
                          title="إعادة تفعيل الحساب بنفس البيانات"
                          onClick={() => openReactivateModal(emp)}
                        >
                          إعادة تفعيل
                        </button>
                      ) : (
                        <span className="hr-emp-protected-tag">
                          {isSameAccount(emp, user) ? "حسابك" : "GM فقط"}
                        </span>
                      )
                    ) : canManage(emp) ? (
                      <>
                        {canResetPassword(emp) ? (
                          <button
                            type="button"
                            className="hr-emp-action-btn reset-password"
                            title="إعادة تعيين كلمة المرور"
                            onClick={() => openResetPasswordModal(emp)}
                          >
                            إعادة تعيين كلمة المرور
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="hr-emp-action-btn edit"
                          title="نقل / ترقية / تعديل"
                          onClick={() => openEditModal(emp)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="hr-emp-action-btn delete"
                          title="إنهاء الخدمة"
                          onClick={() => openDeleteModal(emp)}
                        >
                          إنهاء الخدمة
                        </button>
                      </>
                    ) : (
                      <span className="hr-emp-protected-tag">
                        {isSameAccount(emp, user) ? "حسابك" : "GM فقط"}
                      </span>
                    )}
                    <button
                      type="button"
                      className="hr-emp-action-btn history"
                      title="عرض السجل"
                      onClick={() => setHistoryEmployee(emp)}
                    >
                      السجل
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredEmployees.length === 0 && (
          <p className="hr-emp-empty">لا يوجد أعضاء مطابقين للبحث</p>
        )}
      </div>

      {showAddModal && (
        <div
          className="hr-emp-modal-overlay"
          onClick={() => { setShowAddModal(false); setAddModalTab("new"); }}
        >
          <div className="hr-emp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="hr-emp-modal-title">إضافة عضو</h3>

            {/* Tab switcher */}
            <div className="hr-emp-modal-tabs">
              <button
                type="button"
                className={`hr-emp-modal-tab${addModalTab === "new" ? " active" : ""}`}
                onClick={() => setAddModalTab("new")}
              >
                موظف جديد
              </button>
              <button
                type="button"
                className={`hr-emp-modal-tab${addModalTab === "link" ? " active" : ""}`}
                onClick={() => setAddModalTab("link")}
              >
                ربط حساب بموظف موجود
              </button>
            </div>

            {/* ── TAB: Link existing employee account ── */}
            {addModalTab === "link" && (
              <form onSubmit={handleLinkAccount} className="hr-emp-link-form">
                <p className="hr-emp-form-hint hr-emp-link-intro">
                  أدخل رقم الموظف لإنشاء حساب دخول وربطه تلقائياً بدون تغيير أي بيانات تنظيمية.
                </p>

                <div className="hr-emp-modal-grid">
                  <div className="hr-emp-modal-field hr-emp-modal-field-full">
                    <label>رقم الموظف (Employee Code) *</label>
                    <input
                      type="text"
                      value={linkForm.employeeCode}
                      onChange={(e) => handleLinkCodeChange(e.target.value)}
                      placeholder="مثال: EMP-2026-00001"
                      autoComplete="off"
                    />
                    {lookupLoading && (
                      <p className="hr-emp-form-hint">جاري البحث...</p>
                    )}
                    {!lookupLoading && lookupResult && (
                      <div className={`hr-emp-lookup-result${lookupResult.found && !lookupResult.employee?.hasAccount ? " found" : lookupResult.found && lookupResult.employee?.hasAccount ? " has-account" : " not-found"}`}>
                        {lookupResult.found && !lookupResult.employee?.hasAccount ? (
                          <>
                            <div className="hr-emp-lookup-label">تم العثور على الموظف</div>
                            <div className="hr-emp-lookup-name">{lookupResult.employee.fullName}</div>
                            <div className="hr-emp-lookup-meta">
                              <span>{lookupResult.employee.jobTitle}</span>
                              {lookupResult.employee.department && (
                                <span> · {lookupResult.employee.department.name}</span>
                              )}
                              {lookupResult.employee.branch && (
                                <span> · {lookupResult.employee.branch.name}</span>
                              )}
                            </div>
                            {lookupResult.employee.manager && (
                              <div className="hr-emp-lookup-meta">
                                المدير المباشر: {lookupResult.employee.manager.fullName} ({lookupResult.employee.manager.employeeCode})
                              </div>
                            )}
                            <div className="hr-emp-lookup-meta">
                              تاريخ التعيين: {lookupResult.employee.hireDate?.slice(0, 10)}
                            </div>
                          </>
                        ) : lookupResult.found && lookupResult.employee?.hasAccount ? (
                          <div className="hr-emp-lookup-label">⚠ هذا الموظف لديه حساب دخول بالفعل</div>
                        ) : (
                          <div className="hr-emp-lookup-label">لم يتم العثور على الموظف بهذا الرقم</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="hr-emp-modal-field">
                    <label>البريد الإلكتروني (اختياري)</label>
                    <input
                      type="email"
                      value={linkForm.email}
                      onChange={(e) => setLinkForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="مثال: ahmed@company.com"
                    />
                    <p className="hr-emp-form-hint">
                      بدون بريد يُستخدم اسم مستخدم مشتق من اسم الموظف
                    </p>
                  </div>

                  <div className="hr-emp-modal-field">
                    <label>كلمة المرور *</label>
                    <input
                      type="text"
                      value={linkForm.password}
                      onChange={(e) => setLinkForm((p) => ({ ...p, password: e.target.value }))}
                      placeholder="Password@123"
                    />
                  </div>

                  <div className="hr-emp-modal-field">
                    <label>الدور الوظيفي (Role) *</label>
                    <select
                      value={linkForm.roleSlug}
                      onChange={(e) => setLinkForm((p) => ({ ...p, roleSlug: e.target.value }))}
                    >
                      <option value="employee">موظف (Employee)</option>
                      <option value="manager">مشرف / تيم ليدر (Manager)</option>
                      <option value="branch_manager">مدير فرع (Branch Manager)</option>
                      <option value="regional_manager">مدير منطقة (Regional Manager)</option>
                      <option value="department_manager">مدير إدارة (Dept. Manager)</option>
                      <option value="hr_staff">موظف موارد بشرية (HR Staff)</option>
                      <option value="hr">موارد بشرية (HR)</option>
                      <option value="gm">مدير عام (GM)</option>
                    </select>
                  </div>
                </div>

                <div className="hr-emp-modal-buttons">
                  <button
                    type="button"
                    className="hr-emp-modal-btn secondary"
                    onClick={() => { setShowAddModal(false); setAddModalTab("new"); }}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="hr-emp-modal-btn primary"
                    disabled={
                      linkSubmitting ||
                      !lookupResult?.found ||
                      lookupResult?.employee?.hasAccount
                    }
                  >
                    {linkSubmitting ? "جاري الإنشاء..." : "إنشاء الحساب وربطه"}
                  </button>
                </div>
              </form>
            )}

            {/* ── TAB: New employee with account ── */}
            {addModalTab === "new" && (
            <form onSubmit={handleAddEmployee}>
              <div className="hr-emp-modal-grid">
                <div className="hr-emp-modal-field">
                  <label>الاسم الكامل *</label>
                  <input
                    type="text"
                    value={newEmployee.name}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="مثال: أحمد محمود"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>البريد الإلكتروني</label>
                  <input
                    type="email"
                    value={newEmployee.email}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="مثال: ahmed@company.com (اختياري)"
                  />
                  <p className="hr-emp-form-hint">
                    اختياري — يمكن تسجيل الدخول بالبريد أو بكود الموظف
                  </p>
                </div>
                <div className="hr-emp-modal-field">
                  <label>كود الموظف (للدخول)</label>
                  <input
                    type="text"
                    value={newEmployee.code}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="مثال: EMP-2026-00042 (اختياري)"
                    maxLength={30}
                    autoComplete="off"
                    dir="ltr"
                  />
                  <p className="hr-emp-form-hint">
                    اختياري — اتركه فارغاً ليُنشأ تلقائياً. الموظف يسجّل الدخول
                    بهذا الكود
                  </p>
                </div>

                {newEmployee.memberType !== "department-manager" && (
                  <div className="hr-emp-modal-field hr-emp-modal-field-full">
                    <label>الإدارة / القسم *</label>
                    <select
                      value={newEmployee.departmentId}
                      onChange={(e) =>
                        applyDepartmentSelection(e.target.value)
                      }
                    >
                      <option value="">اختر الإدارة أولاً</option>
                      {formDepartments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="hr-emp-inline-add-btn"
                      onClick={() => setShowNewDepartmentForm((v) => !v)}
                    >
                      {showNewDepartmentForm ? "إلغاء" : "+ إضافة قسم جديد"}
                    </button>
                    <p className="hr-emp-form-hint">
                      مديرو الفروع والمشرفون متاحون فقط لـ{" "}
                      <strong>إدارة التشغيل</strong>.
                    </p>
                    {showNewDepartmentForm && (
                      <div className="hr-emp-inline-form">
                        <div className="hr-emp-inline-form-grid">
                          <div className="hr-emp-modal-field">
                            <label>اسم القسم</label>
                            <input
                              type="text"
                              value={newDepartmentName}
                              onChange={(e) => {
                                setNewDepartmentName(e.target.value);
                                if (!newDepartmentCode.trim()) {
                                  setNewDepartmentCode(
                                    suggestDepartmentCode(e.target.value),
                                  );
                                }
                              }}
                              placeholder="مثال: خدمة العملاء"
                            />
                          </div>
                          <div className="hr-emp-modal-field">
                            <label>كود القسم</label>
                            <input
                              type="text"
                              value={newDepartmentCode}
                              onChange={(e) =>
                                setNewDepartmentCode(
                                  e.target.value.toUpperCase(),
                                )
                              }
                              placeholder="CS"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="hr-emp-inline-save-btn"
                          onClick={handleCreateDepartment}
                          disabled={creatingDepartment}
                        >
                          {creatingDepartment ? "جاري الحفظ..." : "حفظ القسم"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="hr-emp-type-selector">
                {availableMemberTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={`hr-emp-type-btn${
                      newEmployee.memberType === type.id ? " active" : ""
                    }`}
                    onClick={() => handleMemberTypeChange(type.id)}
                  >
                    <span className="hr-emp-type-label">{type.label}</span>
                    <span className="hr-emp-type-desc">{type.description}</span>
                  </button>
                ))}
              </div>

              <div className="hr-emp-modal-grid">
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label>خط المواصلات</label>
                  <select
                    value={newEmployee.lineId || ""}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        lineId: e.target.value,
                      }))
                    }
                  >
                    <option value="">بدون خط</option>
                    {transportLines
                      .filter((line) => line.isActive !== false)
                      .map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.name}
                          {line.area ? ` — ${line.area}` : ""}
                        </option>
                      ))}
                  </select>
                  <p className="hr-emp-form-hint">
                    أتوبيس المنطقة — مستقل عن الفرع والإدارة. موظفو نفس الخط
                    ياخدوا سماح ومواعيد الخط لو المواصلات اتأخرت.
                  </p>
                </div>
                {newEmployee.memberType === "department-manager" ? (
                  <>
                    <div className="hr-emp-modal-field">
                      <label>الإدارة *</label>
                      <select
                        value={newEmployee.departmentId}
                        onChange={(e) => applyDepartmentSelection(e.target.value)}
                      >
                        <option value="">اختر الإدارة</option>
                        {selectableDepartments.map((dept) => {
                          const existingManager = departmentManagers.find(
                            (m) => m.departmentId === dept.id,
                          );
                          const taken = Boolean(existingManager);
                          const hrGmOnly =
                            userRole !== "gm" && dept.id === HR_DEPT_ID;
                          return (
                            <option
                              key={dept.id}
                              value={dept.id}
                              disabled={taken || hrGmOnly}
                            >
                              {dept.name}
                              {taken
                                ? ` (يوجد مدير: ${existingManager.name})`
                                : hrGmOnly
                                  ? " (صلاحية المدير العام)"
                                  : ""}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        type="button"
                        className="hr-emp-inline-add-btn"
                        onClick={() =>
                          setShowNewDepartmentForm((v) => !v)
                        }
                      >
                        {showNewDepartmentForm ? "إلغاء" : "+ إضافة إدارة جديدة"}
                      </button>
                    </div>
                    {showNewDepartmentForm && (
                      <div className="hr-emp-inline-form hr-emp-modal-field-full">
                        <div className="hr-emp-inline-form-grid">
                          <div className="hr-emp-modal-field">
                            <label>اسم الإدارة</label>
                            <input
                              type="text"
                              value={newDepartmentName}
                              onChange={(e) => {
                                setNewDepartmentName(e.target.value);
                                if (!newDepartmentCode.trim()) {
                                  setNewDepartmentCode(
                                    suggestDepartmentCode(e.target.value),
                                  );
                                }
                              }}
                              placeholder="مثال: المبيعات"
                            />
                          </div>
                          <div className="hr-emp-modal-field">
                            <label>كود الإدارة</label>
                            <input
                              type="text"
                              value={newDepartmentCode}
                              onChange={(e) =>
                                setNewDepartmentCode(
                                  e.target.value.toUpperCase(),
                                )
                              }
                              placeholder="SALES"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="hr-emp-inline-save-btn"
                          onClick={handleCreateDepartment}
                          disabled={creatingDepartment}
                        >
                          {creatingDepartment ? "جاري الحفظ..." : "حفظ الإدارة"}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {usesBranchForForm ? (
                      <div className="hr-emp-modal-field">
                        <label>الفرع *</label>
                        <select
                          value={
                            isCashierForm &&
                            newEmployee.branchId === ADMINISTRATION_KEY
                              ? ""
                              : newEmployee.branchId
                          }
                          onChange={(e) => {
                            const branchId = e.target.value;
                            const autoManagerId = pickAutoDirectManagerId(
                              newEmployee.memberType,
                              newEmployee.departmentId,
                              branchId,
                              newEmployee.jobTitle,
                            );
                            setNewEmployee((prev) => ({
                              ...prev,
                              branchId,
                              managerId: autoManagerId,
                              departmentManagerId:
                                prev.memberType === "branch-manager" ||
                                prev.memberType === "supervisor"
                                  ? autoManagerId
                                  : prev.departmentManagerId,
                            }));
                          }}
                        >
                          {isCashierForm ? (
                            <option value="">اختر فرع الكاشير</option>
                          ) : (
                            <option value={ADMINISTRATION_KEY}>
                              مقر التشغيل (الإدارة)
                            </option>
                          )}
                          {operationalBranches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                        <p className="hr-emp-form-hint">
                          {isCashierForm ? (
                            <>
                              الكاشير يتبع <strong>الحسابات</strong> ويرتبط بـ{" "}
                              <strong>مدير الكاشيرية</strong>، مع تحديد الفرع
                              للخطة الأسبوعية وتوجيه السلف.
                            </>
                          ) : (
                            <>
                              اختر فرعاً ميدانياً، أو{" "}
                              <strong>مقر التشغيل</strong> لأقسام المقر (مثل
                              المخزن) تحت مدير القسم أو مدير الإدارة.
                            </>
                          )}
                        </p>
                        {operationalBranches.length === 0 && (
                          <p className="hr-emp-form-hint">
                            لا توجد فروع — يضيفها المدير العام من الهيكل
                            التنظيمي.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="hr-emp-modal-field hr-emp-modal-field-full">
                        <p className="hr-emp-form-hint">
                          {isOperationsDepartment(newEmployee.departmentId)
                            ? "مدير القسم في التشغيل يعمل من مقر الإدارة (مثل المخزن) — بدون فرع ميداني، ويتبع مدير إدارة التشغيل."
                            : `${departmentMap[newEmployee.departmentId] || "هذه الإدارة"} تعمل كأقسام داخل المقر — بدون فروع ميدانية.`}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {newEmployee.memberType === "employee" ? (
                  <>
                    <div className="hr-emp-modal-field">
                      <label>المسمى الوظيفي *</label>
                      <select
                        value={newEmployee.jobTitle}
                        onChange={(e) => {
                          const jobTitle = e.target.value;
                          setNewEmployee((prev) => {
                            const needsBranch = assignmentUsesBranch({
                              departmentId: prev.departmentId,
                              memberType: prev.memberType,
                              jobTitle,
                            });
                            const branchId = needsBranch
                              ? prev.branchId
                              : ADMINISTRATION_KEY;
                            return {
                              ...prev,
                              jobTitle,
                              branchId,
                              managerId: pickAutoDirectManagerId(
                                prev.memberType,
                                prev.departmentId,
                                branchId,
                                jobTitle,
                              ),
                            };
                          });
                        }}
                      >
                        {EMPLOYEE_JOB_TITLES.map((title) => (
                          <option key={title} value={title}>
                            {title}
                          </option>
                        ))}
                      </select>
                    </div>
                    {newEmployee.jobTitle === "أخرى" && (
                      <div className="hr-emp-modal-field">
                        <label>المسمى المخصص *</label>
                        <input
                          type="text"
                          value={newEmployee.customJobTitle}
                          onChange={(e) => {
                            const customJobTitle = e.target.value;
                            const autoManagerId = pickAutoDirectManagerId(
                              newEmployee.memberType,
                              newEmployee.departmentId,
                              newEmployee.branchId,
                              customJobTitle,
                            );
                            setNewEmployee((prev) => {
                              const keepManager =
                                !isHqModeratorTeamLeaderAssignment({
                                  jobTitle: customJobTitle,
                                  branchId: prev.branchId,
                                }) ||
                                marketingDirectManagers.some(
                                  (mgr) => mgr.id === prev.managerId,
                                );
                              return {
                                ...prev,
                                customJobTitle,
                                managerId:
                                  autoManagerId ||
                                  (keepManager ? prev.managerId : ""),
                              };
                            });
                          }}
                          placeholder="اكتب المسمى — مثال: تيم ليدر"
                        />
                      </div>
                    )}
                    <div className="hr-emp-modal-field">
                      <label>المدير المباشر *</label>
                      {isCashierForm ? (
                        <>
                          <input
                            type="text"
                            readOnly
                            value={
                              cashiersManager
                                ? formatManagerOption(
                                    cashiersManager,
                                    departmentMap,
                                    branchMap,
                                  )
                                : ""
                            }
                            placeholder="مدير الكاشيرية"
                          />
                          <p className="hr-emp-form-hint">
                            الكاشير يتبع تلقائياً{" "}
                            <strong>مدير الكاشيرية</strong>.
                          </p>
                          {!cashiersManager && (
                            <p className="hr-emp-form-hint">
                              لا يوجد مدير كاشيرية — أنشئه من حسابات الأدمن
                              أولاً.
                            </p>
                          )}
                        </>
                      ) : !usesBranchForForm || isOpsHqEmployeeForm ? (
                        <>
                          <select
                            value={newEmployee.managerId}
                            onChange={(e) =>
                              setNewEmployee((prev) => ({
                                ...prev,
                                managerId: e.target.value,
                              }))
                            }
                          >
                            <option value="">
                              {isHqModeratorTeamLeaderForm
                                ? "اختر المدير المباشر من التسويق"
                                : officeDirectManagerPlaceholder(
                                    newEmployee.departmentId,
                                  )}
                            </option>
                            {directManagers.map((mgr) => (
                              <option key={mgr.id} value={mgr.id}>
                                {formatManagerOption(
                                  mgr,
                                  departmentMap,
                                  branchMap,
                                )}
                              </option>
                            ))}
                          </select>
                          <p className="hr-emp-form-hint">
                            {isHqModeratorTeamLeaderForm ? (
                              <>
                                تيم ليدر الموديريتور في المقر يتبع مديراً من{" "}
                                <strong>قسم التسويق</strong>، وليس من التشغيل.
                              </>
                            ) : (
                              <>
                                يمكنك اختيار{" "}
                                <strong>
                                  {isAccountsDepartment(
                                    newEmployee.departmentId,
                                  )
                                    ? "رئيس قسم الحسابات أو مدير الإدارة"
                                    : "مدير القسم أو مدير الإدارة"}
                                </strong>{" "}
                                كمدير مباشر للموظف داخل{" "}
                                {isOpsHqEmployeeForm
                                  ? "مقر التشغيل"
                                  : departmentMap[newEmployee.departmentId] ||
                                    "الإدارة"}
                                {isAccountsDepartment(newEmployee.departmentId)
                                  ? " — الكاشير يبقى تحت مدير الكاشيرية."
                                  : "."}
                              </>
                            )}
                          </p>
                          {directManagers.length === 0 && (
                            <p className="hr-emp-form-hint">
                              {isHqModeratorTeamLeaderForm
                                ? "لا يوجد مدير إدارة أو مدير قسم في التسويق — أضفه أولاً ثم أعد المحاولة."
                                : isAccountsDepartment(
                                    newEmployee.departmentId,
                                  )
                                  ? "لا يوجد رئيس قسم حسابات أو مدير إدارة في هذه الإدارة — أضفه أولاً ثم أعد المحاولة."
                                  : "لا يوجد مدير قسم أو مدير إدارة في هذه الإدارة — أضفه أولاً ثم أعد المحاولة."}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <select
                            value={newEmployee.managerId}
                            onChange={(e) =>
                              setNewEmployee((prev) => ({
                                ...prev,
                                managerId: e.target.value,
                              }))
                            }
                          >
                            <option value="">
                              اختر التيم ليدر أو مشرف الفرع
                            </option>
                            {directManagers.map((mgr) => (
                              <option key={mgr.id} value={mgr.id}>
                                {formatManagerOption(
                                  mgr,
                                  departmentMap,
                                  branchMap,
                                )}
                              </option>
                            ))}
                          </select>
                          <p className="hr-emp-form-hint">
                            في التشغيل: المدير المباشر للموظف هو{" "}
                            <strong>التيم ليدر أو مشرف الفرع</strong>.
                          </p>
                          {formBranchBackendId &&
                            supervisorsInBranch.length === 0 && (
                              <p className="hr-emp-form-hint">
                                لا يوجد مشرف / تيم ليدر في هذا الفرع — أضفه أولاً
                                ثم أعد المحاولة.
                              </p>
                            )}
                        </>
                      )}
                    </div>
                  </>
                ) : newEmployee.memberType === "section-manager" ? (
                  <>
                    <div className="hr-emp-modal-field">
                      <label>المسمى الوظيفي *</label>
                      <input
                        type="text"
                        value={newEmployee.jobTitle}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            jobTitle: e.target.value,
                          }))
                        }
                        placeholder={
                          isAccountsDepartment(newEmployee.departmentId)
                            ? "رئيس قسم الحسابات"
                            : "مثال: مدير قسم المخزن، مدير قسم المحاسبة"
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field hr-emp-modal-field-full">
                      <label>المدير المباشر</label>
                      {activeDepartmentManager ? (
                        <>
                          <input
                            type="text"
                            readOnly
                            value={formatManagerOption(
                              activeDepartmentManager,
                              departmentMap,
                              branchMap,
                            )}
                            className="hr-emp-readonly-input"
                          />
                          <p className="hr-emp-form-hint">
                            {isAccountsDepartment(newEmployee.departmentId)
                              ? "رئيس قسم الحسابات يتبع مدير الإدارة إن وُجد. مدير الكاشيرية يتبع رئيس القسم، والكاشير يبقى تحته."
                              : "مدير القسم يتبع مدير الإدارة مباشرة."}
                          </p>
                        </>
                      ) : (
                        <p className="hr-emp-form-hint">
                          يجب اختيار إدارة لها مدير مسجّل أولاً.
                        </p>
                      )}
                    </div>
                  </>
                ) : newEmployee.memberType === "department-manager" ? (
                  <>
                    <div className="hr-emp-modal-field">
                      <label>المسمى الوظيفي</label>
                      <input
                        type="text"
                        value={newEmployee.jobTitle}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            jobTitle: e.target.value,
                          }))
                        }
                        placeholder={
                          newEmployee.departmentId
                            ? `اختياري — مثال: أخصائي تسويق (الرتبة: مدير ${departmentMap[newEmployee.departmentId]})`
                            : "اختياري — مثال: أخصائي تسويق"
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field hr-emp-modal-field-full">
                    <p className="hr-emp-form-hint">
                      الرتبة في الهرم (مدير إدارة) مستقلة عن المسمى الوظيفي —
                      يمكن أن يكون المسمى «أخصائي تسويق» مع بقائه مدير إدارة
                      التسويق في الموافقات.
                    </p>
                    <p className="hr-emp-form-hint">
                      مدير الإدارة مسؤول عن{" "}
                      {newEmployee.departmentId
                        ? departmentMap[newEmployee.departmentId]
                        : "إحدى الإدارات"}{" "}
                      — إدارة التشغيل، التسويق، الموارد البشرية، أو الحسابات.
                    </p>
                    <p className="hr-emp-form-hint">
                      المدير المباشر:{" "}
                      <strong>
                        {gmEmployee
                          ? `${gmEmployee.name} (${gmEmployee.code})`
                          : "المدير العام — يجب إنشاء حسابه أولاً"}
                      </strong>
                    </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hr-emp-modal-field">
                      <label>المسمى الوظيفي *</label>
                      <input
                        type="text"
                        value={newEmployee.jobTitle}
                        onChange={(e) => {
                          const jobTitle = e.target.value;
                          const autoManagerId = pickAutoDirectManagerId(
                            newEmployee.memberType,
                            newEmployee.departmentId,
                            newEmployee.branchId,
                            jobTitle,
                          );
                          setNewEmployee((prev) => {
                            const keepManager =
                              !isHqModeratorTeamLeaderAssignment({
                                jobTitle,
                                branchId: prev.branchId,
                              }) ||
                              marketingDirectManagers.some(
                                (mgr) => mgr.id === prev.managerId,
                              );
                            const nextManagerId =
                              autoManagerId ||
                              (keepManager ? prev.managerId : "");
                            return {
                              ...prev,
                              jobTitle,
                              managerId: nextManagerId,
                              departmentManagerId:
                                prev.memberType === "supervisor"
                                  ? nextManagerId
                                  : prev.departmentManagerId,
                            };
                          });
                        }}
                        placeholder={
                          newEmployee.memberType === "branch-manager"
                            ? "مثال: مدير فرع مدينة نصر"
                            : newEmployee.memberType === "regional-manager"
                              ? "مثال: مدير منطقة القاهرة"
                              : "مثال: تيم ليدر أو مشرف مبيعات"
                        }
                      />
                      {newEmployee.memberType === "supervisor" && (
                        <p className="hr-emp-form-hint">
                          لتيم ليدر الموديريتور: اكتبي «تيم ليدر» واختاري{" "}
                          <strong>مقر التشغيل</strong> ثم مديراً من التسويق.
                        </p>
                      )}
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>
                        {newEmployee.memberType === "regional-manager"
                          ? "مدير إدارة التشغيل *"
                          : newEmployee.memberType === "supervisor"
                            ? isHqModeratorTeamLeaderForm
                              ? "المدير المباشر (التسويق) *"
                              : "مدير الفرع *"
                            : "مدير المنطقة *"}
                      </label>
                      <select
                        value={
                          newEmployee.memberType === "supervisor"
                            ? newEmployee.managerId ||
                              newEmployee.departmentManagerId
                            : newEmployee.departmentManagerId
                        }
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            departmentManagerId: e.target.value,
                            managerId: e.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {newEmployee.memberType === "regional-manager"
                            ? "اختر مدير إدارة التشغيل"
                            : newEmployee.memberType === "supervisor"
                              ? isHqModeratorTeamLeaderForm
                                ? "اختر مديراً من قسم التسويق"
                                : "اختر مدير الفرع"
                              : "اختر مدير المنطقة المسؤول عن الفرع"}
                        </option>
                        {directManagers.map((mgr) => (
                          <option key={mgr.id} value={mgr.id}>
                            {formatManagerOption(mgr, departmentMap, branchMap)}
                          </option>
                        ))}
                      </select>
                      {newEmployee.memberType === "supervisor" &&
                        isHqModeratorTeamLeaderForm &&
                        marketingDirectManagers.length === 0 && (
                          <p className="hr-emp-form-hint">
                            لا يوجد مدير إدارة أو مدير قسم في التسويق — أضفه
                            أولاً ثم أعد المحاولة.
                          </p>
                        )}
                      {newEmployee.memberType === "supervisor" &&
                        !isHqModeratorTeamLeaderForm &&
                        formBranchBackendId &&
                        branchManagersInBranch.length === 0 && (
                          <p className="hr-emp-form-hint">
                            لا يوجد مدير فرع لهذا الفرع — أضفه أولاً ثم أعد
                            المحاولة.
                          </p>
                        )}
                      {newEmployee.memberType === "branch-manager" &&
                        formBranchBackendId &&
                        directManagers.length === 0 && (
                          <p className="hr-emp-form-hint">
                            لا يوجد مدير منطقة مرتبط بهذا الفرع — عيّن مدير
                            منطقة للفرع أولاً.
                          </p>
                        )}
                      {newEmployee.memberType === "regional-manager" &&
                        opsDepartmentManagers.length === 0 && (
                          <p className="hr-emp-form-hint">
                            لا يوجد مدير إدارة تشغيل — أضفه أولاً ثم أعد
                            المحاولة.
                          </p>
                        )}
                    </div>
                    {newEmployee.memberType === "regional-manager" && (
                      <div className="hr-emp-modal-field hr-emp-modal-field-full">
                        <label>الفروع التابعة *</label>
                        <div className="hr-emp-checkbox-grid">
                          {operationalBranches.map((branch) => {
                            const checked = (
                              newEmployee.managedBranchIds || []
                            ).includes(branch.id);
                            return (
                              <label
                                key={branch.id}
                                className="hr-emp-checkbox-row"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    setNewEmployee((prev) => {
                                      const current = prev.managedBranchIds || [];
                                      const next = checked
                                        ? current.filter((id) => id !== branch.id)
                                        : [...current, branch.id];
                                      return {
                                        ...prev,
                                        managedBranchIds: next,
                                      };
                                    })
                                  }
                                />
                                <span>
                                  {branch.name}
                                  {branch.code ? ` (${branch.code})` : ""}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="hr-emp-form-hint">
                          اختر الفروع التي تقع تحت هذا المدير فقط — باقي الفروع
                          لن تظهر في موافقاته وفريقه.
                        </p>
                        {(newEmployee.managedBranchIds || []).length === 0 && (
                          <p className="hr-emp-form-hint">
                            يجب اختيار فرع واحد على الأقل.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="hr-emp-modal-field hr-emp-modal-field-full">
                      <p className="hr-emp-form-hint">
                        {isHqModeratorTeamLeaderForm
                          ? "تيم ليدر الموديريتور في المقر: الموظف → تيم ليدر → مدير من التسويق."
                          : "تسلسل التشغيل: موظف → مشرف/تيم ليدر → مدير فرع → مدير منطقة → مدير إدارة التشغيل."}
                      </p>
                    </div>
                  </>
                )}

                <div className="hr-emp-modal-field">
                  <label>تاريخ الانضمام</label>
                  <input
                    type="date"
                    value={newEmployee.joinDate}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        joinDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>نظام العمل *</label>
                  <select
                    value={newEmployee.workMode || "OFFICE"}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        workMode: e.target.value,
                      }))
                    }
                  >
                    {WORK_MODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="hr-emp-form-hint">
                    {
                      WORK_MODE_OPTIONS.find(
                        (o) => o.value === (newEmployee.workMode || "OFFICE"),
                      )?.hint
                    }
                  </p>
                </div>
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label className="hr-emp-checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(newEmployee.manualAttendanceOnly)}
                      onChange={(e) =>
                        setNewEmployee((prev) => ({
                          ...prev,
                          manualAttendanceOnly: e.target.checked,
                        }))
                      }
                    />
                    ساعات مرنة (حضور افتراضي)
                  </label>
                  <p className="hr-emp-form-hint">
                    لا بصمة إلزامية ولا غياب/تأخير تلقائي — الموظف حضور إلا إذا
                    سجّل الـHR غياباً أو إجازة
                  </p>
                </div>
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label className="hr-emp-checkbox-label">
                    <input
                      type="checkbox"
                      checked={
                        Boolean(newEmployee.requiresShiftHandover) ||
                        isModeratorJobTitle(newEmployee.jobTitle) ||
                        isModeratorJobTitle(newEmployee.customJobTitle)
                      }
                      onChange={(e) =>
                        setNewEmployee((prev) => ({
                          ...prev,
                          requiresShiftHandover: e.target.checked,
                        }))
                      }
                    />
                    استلام شيفت (موديريتور)
                  </label>
                  <p className="hr-emp-form-hint">
                    الانصراف من التطبيق لا يُفتح إلا بعد ما الموديريتور التالية
                    تسجّل حضور وتستلم الشيفت
                  </p>
                </div>
                <div className="hr-emp-modal-field">
                  <label>الحالة</label>
                  <select
                    value={newEmployee.status}
                    onChange={(e) =>
                      setNewEmployee((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="active">نشط</option>
                    <option value="on-leave">في إجازة</option>
                    <option value="inactive">موقوف / غير نشط</option>
                  </select>
                </div>

                {canEditSalary && (
                <div className="hr-emp-modal-section hr-emp-modal-field-full">
                  <h4 className="hr-emp-modal-section-title">الراتب</h4>
                  <div className="hr-emp-modal-section-grid">
                    <div className="hr-emp-modal-field">
                      <label>طريقة القبض</label>
                      <select
                        value={newEmployee.payFrequency || "MONTHLY"}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            payFrequency: e.target.value,
                          }))
                        }
                      >
                        <option value="MONTHLY">شهري</option>
                        <option value="WEEKLY">أسبوعي</option>
                      </select>
                      <p className="hr-emp-form-hint">
                        الأسبوعي: يُدخل للموظف يومية (ثمن يوم الشغل) ويُصرف كل
                        أسبوع × 6 أيام عمل.
                      </p>
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>
                        {newEmployee.payFrequency === "WEEKLY"
                          ? "اليومية (ج.م)"
                          : "الراتب الأساسي (ج.م)"}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={
                          newEmployee.payFrequency === "WEEKLY"
                            ? "مثال: 150"
                            : "مثال: 5000"
                        }
                        value={newEmployee.salary}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            salary: e.target.value,
                          }))
                        }
                      />
                      <p className="hr-emp-form-hint">
                        {newEmployee.payFrequency === "WEEKLY"
                          ? "ثمن يوم الشغل. الأسبوع يُصرف = اليومية × 6 أيام عمل (الجمعة لا تُحسب)."
                          : "الراتب الشهري الأساسي المستخدم في الرواتب — يمكن تركه فارغاً وإضافته لاحقاً."}
                      </p>
                    </div>
                  </div>
                </div>
                )}

                <div className="hr-emp-modal-section hr-emp-modal-field-full">
                  <h4 className="hr-emp-modal-section-title">رصيد الإجازات</h4>
                  <div className="hr-emp-modal-section-grid">
                    <div className="hr-emp-modal-field">
                      <label>إجازة اعتيادية — المستحق</label>
                      <input
                        type="number"
                        min="0"
                        value={newEmployee.annualEntitlement}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            annualEntitlement: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                    <label>إجازة اعتيادية — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newEmployee.annualUsed}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            annualUsed: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>إجازة عارضة — المستحق</label>
                      <input
                        type="number"
                        min="0"
                        value={newEmployee.casualEntitlement}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            casualEntitlement: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                    <label>إجازة عارضة — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newEmployee.casualUsed}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            casualUsed: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>إجازة مرضية — المستحق</label>
                      <input
                        type="number"
                        min="0"
                        value={newEmployee.sickEntitlement}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            sickEntitlement: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                    <label>إجازة مرضية — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newEmployee.sickUsed}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            sickUsed: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="hr-emp-modal-section hr-emp-modal-field-full">
                  <h4 className="hr-emp-modal-section-title">سلفة عادية</h4>
                  <p className="hr-emp-form-hint">
                    تُخصم تلقائياً من مرتب الشهر التالي.
                  </p>
                  <div className="hr-emp-modal-section-grid">
                    <div className="hr-emp-modal-field">
                      <label>المبلغ (ج.م)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newEmployee.ordinaryAdvanceOutstanding}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            ordinaryAdvanceOutstanding: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="hr-emp-modal-section hr-emp-modal-field-full">
                  <h4 className="hr-emp-modal-section-title">سلفة استثنائية</h4>
                  <p className="hr-emp-form-hint">
                    المبلغ الكامل ÷ عدد الأقساط = القسط الشهري. يُخصم أول يوم
                    في الشهر التالي. المتبقي يظهر للموظف في الرئيسية.
                  </p>
                  <div className="hr-emp-modal-section-grid">
                    <div className="hr-emp-modal-field">
                      <label>المبلغ الكامل (ج.م)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newEmployee.exceptionalAdvanceTotalAmount}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            exceptionalAdvanceTotalAmount: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>عدد الأقساط</label>
                      <input
                        type="number"
                        min="0"
                        value={newEmployee.exceptionalAdvanceInstallmentsTotal}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            exceptionalAdvanceInstallmentsTotal: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>القسط الشهري (ج.م)</label>
                      <input
                        type="number"
                        readOnly
                        tabIndex={-1}
                        value={
                          getExceptionalAdvanceFormPreview(newEmployee)
                            .monthly || ""
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>الأقساط المدفوعة</label>
                      <input
                        type="number"
                        min="0"
                        value={newEmployee.exceptionalAdvanceInstallmentsPaid}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({
                            ...prev,
                            exceptionalAdvanceInstallmentsPaid: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="hr-emp-modal-field">
                      <label>المتبقي (ج.م)</label>
                      <input
                        type="number"
                        readOnly
                        tabIndex={-1}
                        value={
                          getExceptionalAdvanceFormPreview(newEmployee)
                            .outstanding || ""
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="hr-emp-modal-section hr-emp-modal-field-full">
                  <div className="hr-emp-modal-section-header">
                    <h4 className="hr-emp-modal-section-title">جزاءات سابقة</h4>
                    <button
                      type="button"
                      className="hr-emp-inline-add-btn"
                      onClick={() =>
                        setNewEmployee((prev) => ({
                          ...prev,
                          initialPenalties: [
                            ...prev.initialPenalties,
                            { ...EMPTY_PENALTY },
                          ],
                        }))
                      }
                    >
                      + إضافة جزاء
                    </button>
                  </div>
                  {newEmployee.initialPenalties.length === 0 ? (
                    <p className="hr-emp-form-hint">
                      اختياري — أضف جزاءات مسجلة مسبقاً للموظف
                    </p>
                  ) : (
                    newEmployee.initialPenalties.map((penalty, index) => (
                      <div key={index} className="hr-emp-penalty-row">
                        <div className="hr-emp-modal-section-grid">
                          <div className="hr-emp-modal-field">
                            <label>نوع الجزاء</label>
                            <select
                              value={penalty.type}
                              onChange={(e) =>
                                setNewEmployee((prev) => ({
                                  ...prev,
                                  initialPenalties: prev.initialPenalties.map(
                                    (p, i) =>
                                      i === index
                                        ? { ...p, type: e.target.value }
                                        : p,
                                  ),
                                }))
                              }
                            >
                              {PENALTY_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="hr-emp-modal-field">
                            <label>تاريخ الجزاء</label>
                            <input
                              type="date"
                              value={penalty.effectiveDate}
                              onChange={(e) =>
                                setNewEmployee((prev) => ({
                                  ...prev,
                                  initialPenalties: prev.initialPenalties.map(
                                    (p, i) =>
                                      i === index
                                        ? {
                                            ...p,
                                            effectiveDate: e.target.value,
                                          }
                                        : p,
                                  ),
                                }))
                              }
                            />
                          </div>
                          <div className="hr-emp-modal-field">
                            <label>عدد الأيام</label>
                            <input
                              type="number"
                              min="0"
                              step="0.25"
                              value={penalty.days}
                              onChange={(e) =>
                                setNewEmployee((prev) => ({
                                  ...prev,
                                  initialPenalties: prev.initialPenalties.map(
                                    (p, i) =>
                                      i === index
                                        ? { ...p, days: e.target.value }
                                        : p,
                                  ),
                                }))
                              }
                            />
                          </div>
                          {penalty.type === "FINANCIAL_PENALTY" && (
                            <div className="hr-emp-modal-field">
                              <label>المبلغ (ج.م)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={penalty.amount}
                                onChange={(e) =>
                                  setNewEmployee((prev) => ({
                                    ...prev,
                                    initialPenalties:
                                      prev.initialPenalties.map((p, i) =>
                                        i === index
                                          ? { ...p, amount: e.target.value }
                                          : p,
                                      ),
                                  }))
                                }
                              />
                            </div>
                          )}
                          <div className="hr-emp-modal-field hr-emp-modal-field-full">
                            <label>السبب</label>
                            <input
                              type="text"
                              value={penalty.reason}
                              placeholder="سبب الجزاء"
                              onChange={(e) =>
                                setNewEmployee((prev) => ({
                                  ...prev,
                                  initialPenalties: prev.initialPenalties.map(
                                    (p, i) =>
                                      i === index
                                        ? { ...p, reason: e.target.value }
                                        : p,
                                  ),
                                }))
                              }
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="hr-emp-penalty-remove-btn"
                          onClick={() =>
                            setNewEmployee((prev) => ({
                              ...prev,
                              initialPenalties: prev.initialPenalties.filter(
                                (_, i) => i !== index,
                              ),
                            }))
                          }
                        >
                          حذف الجزاء
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="hr-emp-modal-buttons">
                <button
                  type="button"
                  className="hr-emp-modal-btn secondary"
                  onClick={() => { setShowAddModal(false); setAddModalTab("new"); }}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="hr-emp-modal-btn primary"
                  disabled={apiSubmitting}
                >
                  {apiSubmitting ? "جاري الإنشاء..." : "إضافة العضو"}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {showEditModal && selectedEmployee && (
        <div
          className="hr-emp-modal-overlay"
          onClick={() => {
            if (!editSubmitting) {
              setShowEditModal(false);
              setEditForm(EMPTY_EDIT_FORM);
            }
          }}
        >
          <div
            className="hr-emp-modal hr-emp-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="hr-emp-modal-title">
              تعديل بيانات الموظف — {selectedEmployee.name}
            </h3>
            <p className="hr-emp-form-hint">
              عدّل الاسم والبيانات الشخصية، أو انقل الموظف وغيّر رتبته
              {canEditSalary ? " وراتبه" : ""} ورصيد الإجازات. (
              {selectedEmployee.code})
            </p>

            <div className="hr-emp-modal-section hr-emp-modal-section-first">
              <h4 className="hr-emp-modal-section-title">البيانات الشخصية</h4>
              <div className="hr-emp-modal-section-grid">
                <div className="hr-emp-modal-field">
                  <label>الاسم الكامل *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    maxLength={150}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="مثال: أحمد محمود"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>الاسم كما في البطاقة</label>
                  <input
                    type="text"
                    value={editForm.legalFullName}
                    maxLength={150}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        legalFullName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>كود الموظف</label>
                  <input
                    type="text"
                    value={selectedEmployee.code || ""}
                    readOnly
                    className="hr-emp-readonly-input"
                    dir="ltr"
                  />
                  <p className="hr-emp-form-hint">
                    كود الدخول لا يتغيّر من هنا
                  </p>
                </div>
                <div className="hr-emp-modal-field">
                  <label>البريد الإلكتروني</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="اختياري"
                    dir="ltr"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>رقم الهاتف</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    maxLength={20}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    dir="ltr"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>رقم إضافي</label>
                  <input
                    type="text"
                    value={editForm.phoneSecondary}
                    maxLength={20}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phoneSecondary: e.target.value,
                      }))
                    }
                    dir="ltr"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>الرقم القومي</label>
                  <input
                    type="text"
                    value={editForm.nationalId}
                    maxLength={30}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        nationalId: e.target.value,
                      }))
                    }
                    dir="ltr"
                  />
                </div>
                <div className="hr-emp-modal-field">
                  <label>النوع</label>
                  <select
                    value={editForm.gender}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        gender: e.target.value,
                      }))
                    }
                  >
                    <option value="">اختر</option>
                    {GENDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="hr-emp-modal-field">
                  <label>تاريخ الميلاد</label>
                  <input
                    type="date"
                    value={editForm.birthDate || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        birthDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label>العنوان</label>
                  <textarea
                    className="hr-emp-modal-textarea"
                    rows={2}
                    maxLength={500}
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <h4 className="hr-emp-modal-section-title hr-emp-rank-section-title">
              الرتبة والنقل
            </h4>
            <div className="hr-emp-type-selector">
              {editMemberTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  className={`hr-emp-type-btn${
                    editForm.memberType === type.id ? " active" : ""
                  }`}
                  onClick={() => {
                    const hqModeratorTl = isHqModeratorTeamLeaderAssignment({
                      jobTitle: editForm.jobTitle,
                      branchId: editForm.branchId,
                    });
                    const forceOps =
                      isOpsLockedMemberType(type.id, {
                        jobTitle: editForm.jobTitle,
                        branchId: editForm.branchId,
                      }) && !hqModeratorTl;
                    const nextDept = forceOps
                      ? OPS_DEPT_ID
                      : editForm.departmentId;
                    const keepCustomTitle =
                      type.id !== "employee" &&
                      editForm.jobTitle?.trim() &&
                      !isAutoHierarchyJobTitle(
                        editForm.jobTitle,
                        editForm.memberType,
                      );
                    const defaultTitle =
                      type.id === "employee"
                        ? editForm.memberType === "employee"
                          ? editForm.jobTitle
                          : "كاشير"
                        : type.defaultJobTitle ||
                          (type.id === "section-manager" ? "مدير قسم" : "");
                    const nextJobTitle = keepCustomTitle
                      ? editForm.jobTitle
                      : defaultTitle || editForm.jobTitle;
                    const nextBranchId =
                      forceOps && type.id !== "section-manager"
                        ? editForm.branchId
                        : ADMINISTRATION_KEY;
                    const autoManagerId =
                      type.id === "department-manager"
                        ? ""
                        : pickAutoDirectManagerId(
                            type.id,
                            nextDept,
                            nextBranchId,
                            nextJobTitle,
                          );
                    setEditForm((prev) => ({
                      ...prev,
                      memberType: type.id,
                      departmentId: nextDept,
                      branchId: nextBranchId,
                      jobTitle: nextJobTitle,
                      customJobTitle: "",
                      managerId: autoManagerId,
                      managedBranchIds:
                        type.id === "regional-manager"
                          ? prev.managedBranchIds
                          : [],
                    }));
                  }}
                >
                  <span className="hr-emp-type-label">{type.label}</span>
                  <span className="hr-emp-type-desc">{type.description}</span>
                </button>
              ))}
            </div>

            <div className="hr-emp-modal-grid">
              <div className="hr-emp-modal-field hr-emp-modal-field-full">
                <label>تاريخ التعيين *</label>
                <input
                  type="date"
                  value={editForm.joinDate || ""}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      joinDate: e.target.value,
                    }))
                  }
                />
                {!editForm.joinDate && (
                  <p className="hr-emp-form-hint">
                    اختر تاريخ تعيين الموظف.
                  </p>
                )}
              </div>

              <div className="hr-emp-modal-field">
                <label>نظام العمل *</label>
                <select
                  value={editForm.workMode || "OFFICE"}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      workMode: e.target.value,
                    }))
                  }
                >
                  {WORK_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="hr-emp-form-hint">
                  {
                    WORK_MODE_OPTIONS.find(
                      (o) => o.value === (editForm.workMode || "OFFICE"),
                    )?.hint
                  }
                </p>
              </div>

              <div className="hr-emp-modal-field hr-emp-modal-field-full">
                <label className="hr-emp-checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(editForm.manualAttendanceOnly)}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        manualAttendanceOnly: e.target.checked,
                      }))
                    }
                  />
                  ساعات مرنة (حضور افتراضي)
                </label>
                <p className="hr-emp-form-hint">
                  لا بصمة إلزامية ولا غياب/تأخير تلقائي — الموظف حضور إلا إذا
                  سجّل الـHR غياباً أو إجازة
                </p>
              </div>

              <div className="hr-emp-modal-field hr-emp-modal-field-full">
                <label className="hr-emp-checkbox-label">
                  <input
                    type="checkbox"
                    checked={
                      Boolean(editForm.requiresShiftHandover) ||
                      isModeratorJobTitle(editForm.jobTitle) ||
                      isModeratorJobTitle(editForm.customJobTitle)
                    }
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        requiresShiftHandover: e.target.checked,
                      }))
                    }
                  />
                  استلام شيفت (موديريتور)
                </label>
                <p className="hr-emp-form-hint">
                  الانصراف من التطبيق لا يُفتح إلا بعد ما الموديريتور التالية
                  تسجّل حضور وتستلم الشيفت
                </p>
              </div>

              <div className="hr-emp-modal-field">
                <label>الإدارة *</label>
                <select
                  value={editForm.departmentId}
                  disabled={isOpsLockedMemberType(editForm.memberType, {
                    jobTitle: editForm.jobTitle,
                    branchId: editForm.branchId,
                  })}
                  onChange={(e) => {
                    const departmentId = e.target.value;
                    setEditForm((prev) => {
                      const memberType =
                        !departmentUsesBranches(departmentId) &&
                        isOpsLockedMemberType(prev.memberType, {
                          jobTitle: prev.jobTitle,
                          branchId: prev.branchId,
                        })
                          ? "employee"
                          : prev.memberType;
                      const needsBranch = assignmentUsesBranch({
                        departmentId,
                        memberType,
                        jobTitle: prev.jobTitle,
                      });
                      const branchId = needsBranch
                        ? prev.branchId
                        : ADMINISTRATION_KEY;
                      return {
                        ...prev,
                        departmentId,
                        memberType,
                        branchId,
                        managerId:
                          memberType === "department-manager"
                            ? ""
                            : pickAutoDirectManagerId(
                                memberType,
                                departmentId,
                                branchId,
                                prev.jobTitle,
                              ),
                      };
                    });
                  }}
                >
                  <option value="">اختر الإدارة</option>
                  {(() => {
                    const options = isHqModeratorTeamLeaderAssignment({
                      jobTitle: editForm.jobTitle,
                      branchId: editForm.branchId,
                    })
                      ? departments.filter((d) => d.isActive !== false)
                      : getDepartmentsForMemberType(
                          editForm.memberType,
                          departments,
                        );
                    const current = departments.find(
                      (dept) => dept.id === editForm.departmentId,
                    );
                    const deptOptions =
                      current &&
                      !options.some((dept) => dept.id === current.id)
                        ? [current, ...options]
                        : options;
                    return deptOptions.map((dept) => {
                      const hrGmOnly =
                        userRole !== "gm" &&
                        userRole !== "admin" &&
                        dept.id === HR_DEPT_ID &&
                        editForm.memberType === "department-manager";
                      return (
                        <option
                          key={dept.id}
                          value={dept.id}
                          disabled={hrGmOnly}
                        >
                          {dept.name}
                          {hrGmOnly ? " (صلاحية المدير العام)" : ""}
                        </option>
                      );
                    });
                  })()}
                </select>
                {isOpsLockedMemberType(editForm.memberType, {
                  jobTitle: editForm.jobTitle,
                  branchId: editForm.branchId,
                }) && (
                  <p className="hr-emp-form-hint">
                    هذه الرتبة مرتبطة بإدارة التشغيل فقط.
                  </p>
                )}
              </div>

              {assignmentUsesBranch({
                departmentId: editForm.departmentId,
                memberType: editForm.memberType,
                jobTitle: editForm.jobTitle,
              }) && (
                  <div className="hr-emp-modal-field">
                    <label>الفرع{isCashierJobTitle(editForm.jobTitle) ? " *" : ""}</label>
                    <select
                      value={
                        isCashierJobTitle(editForm.jobTitle) &&
                        editForm.branchId === ADMINISTRATION_KEY
                          ? ""
                          : editForm.branchId
                      }
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          branchId: e.target.value,
                          managerId: isCashierJobTitle(prev.jobTitle)
                            ? prev.managerId
                            : "",
                        }))
                      }
                    >
                      {isCashierJobTitle(editForm.jobTitle) ? (
                        <option value="">اختر فرع الكاشير</option>
                      ) : (
                        <option value={ADMINISTRATION_KEY}>
                          مقر التشغيل (الإدارة)
                        </option>
                      )}
                      {operationalBranches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                    {isCashierJobTitle(editForm.jobTitle) && (
                      <p className="hr-emp-form-hint">
                        فرع الكاشير مطلوب للخطة الأسبوعية وتوجيه السلف من المحاسب العام.
                      </p>
                    )}
                  </div>
                )}

              <div className="hr-emp-modal-field hr-emp-modal-field-full">
                <label>خط المواصلات</label>
                <select
                  value={editForm.lineId || ""}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      lineId: e.target.value,
                    }))
                  }
                >
                  <option value="">بدون خط</option>
                  {transportLines
                    .filter(
                      (line) =>
                        line.isActive !== false || line.id === editForm.lineId,
                    )
                    .map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.name}
                        {line.area ? ` — ${line.area}` : ""}
                      </option>
                    ))}
                </select>
                <p className="hr-emp-form-hint">
                  أتوبيس المنطقة — مستقل عن الفرع والإدارة.
                </p>
              </div>

              {editForm.memberType === "employee" ? (
                <>
                  <div className="hr-emp-modal-field">
                    <label>المسمى الوظيفي *</label>
                    <select
                      value={editForm.jobTitle}
                      onChange={(e) => {
                        const jobTitle = e.target.value;
                        setEditForm((prev) => {
                          const needsBranch = assignmentUsesBranch({
                            departmentId: prev.departmentId,
                            memberType: prev.memberType,
                            jobTitle,
                          });
                          return {
                            ...prev,
                            jobTitle,
                            customJobTitle:
                              jobTitle === "أخرى" ? prev.customJobTitle : "",
                            branchId: needsBranch
                              ? prev.branchId
                              : ADMINISTRATION_KEY,
                            managerId: isCashierJobTitle(jobTitle)
                              ? cashiersManager?.id || prev.managerId
                              : prev.managerId,
                          };
                        });
                      }}
                    >
                      {EMPLOYEE_JOB_TITLES.map((title) => (
                        <option key={title} value={title}>
                          {title}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editForm.jobTitle === "أخرى" && (
                    <div className="hr-emp-modal-field">
                      <label>المسمى المخصص *</label>
                      <input
                        type="text"
                        value={editForm.customJobTitle}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            customJobTitle: e.target.value,
                          }))
                        }
                        placeholder="اكتب المسمى الوظيفي"
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="hr-emp-modal-field">
                  <label>المسمى الوظيفي *</label>
                  <input
                    type="text"
                    value={editForm.jobTitle}
                    onChange={(e) => {
                      const jobTitle = e.target.value;
                      setEditForm((prev) => {
                        const becomesHqTl = isHqModeratorTeamLeaderAssignment({
                          jobTitle,
                          branchId: prev.branchId,
                        });
                        const keepManager =
                          !becomesHqTl ||
                          marketingDirectManagers.some(
                            (mgr) => mgr.id === prev.managerId,
                          );
                        return {
                          ...prev,
                          jobTitle,
                          managerId: keepManager ? prev.managerId : "",
                        };
                      });
                    }}
                    placeholder={
                      editForm.memberType === "department-manager"
                        ? "مثال: أخصائي تسويق"
                        : "المسمى الوظيفي"
                    }
                  />
                  {editForm.memberType === "department-manager" && (
                    <p className="hr-emp-form-hint">
                      الرتبة (مدير إدارة) من اختيار النوع أعلاه — المسمى الوظيفي
                      يمكن أن يكون مختلفاً مثل «أخصائي تسويق».
                    </p>
                  )}
                </div>
              )}

              {canEditSalary && (
              <>
              <div className="hr-emp-modal-field">
                <label>طريقة القبض</label>
                <select
                  value={editForm.payFrequency || "MONTHLY"}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      payFrequency: e.target.value,
                    }))
                  }
                >
                  <option value="MONTHLY">شهري</option>
                  <option value="WEEKLY">أسبوعي</option>
                </select>
                <p className="hr-emp-form-hint">
                  الأسبوعي: يُدخل للموظف يومية (ثمن يوم الشغل) ويُصرف كل أسبوع
                  × 6 أيام عمل.
                </p>
              </div>
              <div className="hr-emp-modal-field">
                <label>
                  {editForm.payFrequency === "WEEKLY"
                    ? "اليومية (ج.م)"
                    : "الراتب الأساسي (ج.م)"}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={
                    editForm.payFrequency === "WEEKLY"
                      ? "مثال: 150"
                      : "مثال: 5000"
                  }
                  value={editForm.salary}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      salary: e.target.value,
                    }))
                  }
                />
                <p className="hr-emp-form-hint">
                  {editForm.payFrequency === "WEEKLY"
                    ? "ثمن يوم الشغل. الأسبوع يُصرف = اليومية × 6 أيام عمل (الجمعة لا تُحسب)."
                    : "اتركه فارغاً إذا لا تريد تغيير الراتب الحالي."}
                </p>
              </div>
              </>
              )}

              {editForm.memberType !== "department-manager" && (
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label>المدير المباشر *</label>
                  {isCashierJobTitle(editForm.jobTitle) ? (
                    <>
                      <input
                        type="text"
                        readOnly
                        value={
                          cashiersManager
                            ? formatManagerOption(
                                cashiersManager,
                                departmentMap,
                                branchMap,
                              )
                            : ""
                        }
                        placeholder="مدير الكاشيرية"
                      />
                      <p className="hr-emp-form-hint">
                        الكاشير يتبع تلقائياً{" "}
                        <strong>مدير الكاشيرية</strong>.
                      </p>
                      {!cashiersManager && (
                        <p className="hr-emp-form-hint">
                          لا يوجد مدير كاشيرية — أنشئه من حسابات الأدمن
                          أولاً.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <select
                        value={editForm.managerId}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            managerId: e.target.value,
                          }))
                        }
                      >
                        <option value="">اختر المدير المباشر</option>
                        {getEditManagerOptions().map((mgr) => (
                          <option key={mgr.id} value={mgr.id}>
                            {formatManagerOption(mgr, departmentMap, branchMap)}
                          </option>
                        ))}
                      </select>
                      {isHqModeratorTeamLeaderAssignment({
                        jobTitle: resolveEditJobTitle(),
                        branchId: editForm.branchId,
                      }) && (
                        <p className="hr-emp-form-hint">
                          تيم ليدر الموديريتور في المقر يتبع مديراً من{" "}
                          <strong>قسم التسويق</strong>، وليس من التشغيل. لو
                          كانت على فرع ميداني، انقليها إلى مقر التشغيل أولاً.
                        </p>
                      )}
                      {isCashierManagerJobTitle(editForm.jobTitle) && (
                        <p className="hr-emp-form-hint">
                          مدير الكاشيرية يتبع{" "}
                          <strong>رئيس قسم الحسابات</strong>. الكاشير يظل تحته.
                        </p>
                      )}
                      {editForm.memberType === "employee" &&
                        !isCashierJobTitle(editForm.jobTitle) &&
                        (!isOperationsDepartment(editForm.departmentId) ||
                          isAdministrationAssignment(editForm.branchId)) && (
                          <p className="hr-emp-form-hint">
                            يمكنك اختيار{" "}
                            <strong>
                              {isAccountsDepartment(editForm.departmentId)
                                ? "رئيس قسم الحسابات أو مدير الإدارة"
                                : "مدير القسم أو مدير الإدارة"}
                            </strong>{" "}
                            كمدير مباشر.
                          </p>
                        )}
                    </>
                  )}
                </div>
              )}

              {editForm.memberType === "department-manager" && (
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <p className="hr-emp-form-hint">
                    في الهرم الوظيفي: مدير إدارة
                    {editForm.departmentId
                      ? ` ${departmentMap[editForm.departmentId] || ""}`
                      : ""}
                    . المدير المباشر:{" "}
                    <strong>
                      {gmEmployee
                        ? `${gmEmployee.name} (${gmEmployee.code})`
                        : "المدير العام"}
                    </strong>
                  </p>
                </div>
              )}

              {editForm.memberType === "regional-manager" && (
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label>الفروع التابعة *</label>
                  <div className="hr-emp-checkbox-grid">
                    {operationalBranches.map((branch) => {
                      const checked = (
                        editForm.managedBranchIds || []
                      ).includes(branch.id);
                      return (
                        <label
                          key={branch.id}
                          className="hr-emp-checkbox-row"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setEditForm((prev) => {
                                const current = prev.managedBranchIds || [];
                                const next = checked
                                  ? current.filter((id) => id !== branch.id)
                                  : [...current, branch.id];
                                return {
                                  ...prev,
                                  managedBranchIds: next,
                                };
                              })
                            }
                          />
                          <span>
                            {branch.name}
                            {branch.code ? ` (${branch.code})` : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="hr-emp-modal-section hr-emp-modal-field-full">
                <h4 className="hr-emp-modal-section-title">رصيد الإجازات</h4>
                <div className="hr-emp-modal-section-grid">
                  <div className="hr-emp-modal-field">
                    <label>إجازة اعتيادية — المستحق</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.annualEntitlement}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          annualEntitlement: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>إجازة اعتيادية — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editForm.annualUsed}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          annualUsed: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>إجازة عارضة — المستحق</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.casualEntitlement}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          casualEntitlement: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>إجازة عارضة — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editForm.casualUsed}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          casualUsed: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>إجازة مرضية — المستحق</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.sickEntitlement}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          sickEntitlement: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>إجازة مرضية — المستخدم</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editForm.sickUsed}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          sickUsed: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="hr-emp-modal-section hr-emp-modal-field-full">
                <h4 className="hr-emp-modal-section-title">سلفة عادية</h4>
                <p className="hr-emp-form-hint">
                  تُخصم تلقائياً من مرتب الشهر التالي.
                </p>
                <div className="hr-emp-modal-section-grid">
                  <div className="hr-emp-modal-field">
                    <label>المبلغ (ج.م)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.ordinaryAdvanceOutstanding}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          ordinaryAdvanceOutstanding: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="hr-emp-modal-section hr-emp-modal-field-full">
                <h4 className="hr-emp-modal-section-title">سلفة استثنائية</h4>
                <p className="hr-emp-form-hint">
                  يمكن تسجيل سلفة عادية وسلفة استثنائية لنفس الموظف. المبلغ
                  الكامل ÷ عدد الأقساط = القسط الشهري، ويُخصم أول يوم في الشهر
                  التالي. المتبقي يظهر في الرئيسية.
                </p>
                <div className="hr-emp-modal-section-grid">
                  <div className="hr-emp-modal-field">
                    <label>المبلغ الكامل (ج.م)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.exceptionalAdvanceTotalAmount}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          exceptionalAdvanceTotalAmount: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>عدد الأقساط</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.exceptionalAdvanceInstallmentsTotal}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          exceptionalAdvanceInstallmentsTotal: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>القسط الشهري (ج.م)</label>
                    <input
                      type="number"
                      readOnly
                      tabIndex={-1}
                      value={
                        getExceptionalAdvanceFormPreview(editForm).monthly ||
                        ""
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>الأقساط المدفوعة</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.exceptionalAdvanceInstallmentsPaid}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          exceptionalAdvanceInstallmentsPaid: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="hr-emp-modal-field">
                    <label>المتبقي (ج.م)</label>
                    <input
                      type="number"
                      readOnly
                      tabIndex={-1}
                      value={
                        getExceptionalAdvanceFormPreview(editForm)
                          .outstanding || ""
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="hr-emp-modal-buttons">
              <button
                type="button"
                className="hr-emp-modal-btn secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditForm(EMPTY_EDIT_FORM);
                }}
                disabled={editSubmitting}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="hr-emp-modal-btn primary"
                onClick={confirmEdit}
                disabled={editSubmitting}
              >
                {editSubmitting ? "جاري الحفظ..." : "حفظ التعديلات"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedEmployee && (
        <div
          className="hr-emp-modal-overlay"
          onClick={() => {
            if (!deleteSubmitting) {
              setShowDeleteModal(false);
              setTerminateReason("");
            }
          }}
        >
          <div
            className="hr-emp-modal delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="hr-emp-modal-title">⚠️ تأكيد إنهاء الخدمة</h3>
            <p className="hr-emp-modal-text">
              هل أنت متأكد من إنهاء خدمة <strong>{selectedEmployee.name}</strong> (
              {selectedEmployee.code})
              {selectedEmployee.departmentId
                ? ` — ${departmentMap[selectedEmployee.departmentId]}`
                : selectedEmployee.branchId &&
                    selectedEmployee.branchId !== ADMINISTRATION_KEY
                  ? ` — ${getBranchLabel(selectedEmployee.branchId, branchMap)}`
                  : " — الإدارة"}
              ؟
            </p>
            <p className="hr-emp-modal-warning">
              سيتم إيقاف حساب الدخول ولن يتمكن من تسجيل الدخول مجدداً. سيُزال من
              التايم شيت من اليوم فصاعداً. بيانات الموظف وسجلّه السابق (إجازات،
              حضور، جزاءات...) ستبقى محفوظة في النظام.
            </p>
            <div className="hr-emp-modal-field hr-emp-modal-field-full">
              <label htmlFor="terminate-reason">
                سبب إنهاء الخدمة <span className="hr-emp-required">*</span>
              </label>
              <textarea
                id="terminate-reason"
                className="hr-emp-modal-textarea"
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                placeholder="اكتب سبب إنهاء الخدمة..."
                rows={3}
                maxLength={500}
                disabled={deleteSubmitting}
                required
              />
              <span className="hr-emp-field-hint">
                {terminateReason.trim().length}/500 — سيظهر السبب في سجل الموظف
              </span>
            </div>
            <div className="hr-emp-modal-buttons">
              <button
                className="hr-emp-modal-btn secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTerminateReason("");
                }}
                disabled={deleteSubmitting}
              >
                إلغاء
              </button>
              <button
                className="hr-emp-modal-btn danger"
                onClick={confirmDelete}
                disabled={deleteSubmitting || terminateReason.trim().length < 3}
              >
                {deleteSubmitting ? "جاري التنفيذ..." : "تأكيد إنهاء الخدمة"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReactivateModal && selectedEmployee && (
        <div
          className="hr-emp-modal-overlay"
          onClick={() => {
            if (!reactivateSubmitting) {
              setShowReactivateModal(false);
              setReactivateNotes("");
            }
          }}
        >
          <div
            className="hr-emp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="hr-emp-modal-title">إعادة تفعيل الحساب</h3>
            <p className="hr-emp-modal-text">
              هل تريد إعادة تفعيل <strong>{selectedEmployee.name}</strong> (
              {selectedEmployee.code}) بنفس بياناته الحالية؟
            </p>
            <p className="hr-emp-modal-warning hr-emp-modal-warning-info">
              سيتم استعادة الحالة إلى «نشط» وإعادة فتح حساب الدخول بنفس الكود
              والبيانات والسجل السابق. لن يتم إنشاء ملف موظف جديد.
            </p>
            <div className="hr-emp-modal-field hr-emp-modal-field-full">
              <label htmlFor="reactivate-notes">ملاحظة (اختياري)</label>
              <textarea
                id="reactivate-notes"
                className="hr-emp-modal-textarea"
                value={reactivateNotes}
                onChange={(e) => setReactivateNotes(e.target.value)}
                placeholder="مثال: عودة للعمل بعد الاستقالة..."
                rows={3}
                maxLength={500}
                disabled={reactivateSubmitting}
              />
              <span className="hr-emp-field-hint">
                {reactivateNotes.trim().length}/500 — ستظهر في سجل الموظف
              </span>
            </div>
            <div className="hr-emp-modal-buttons">
              <button
                className="hr-emp-modal-btn secondary"
                onClick={() => {
                  setShowReactivateModal(false);
                  setReactivateNotes("");
                }}
                disabled={reactivateSubmitting}
              >
                إلغاء
              </button>
              <button
                className="hr-emp-modal-btn primary"
                onClick={confirmReactivate}
                disabled={reactivateSubmitting}
              >
                {reactivateSubmitting
                  ? "جاري التفعيل..."
                  : "تأكيد إعادة التفعيل"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResetPasswordModal && selectedEmployee && (
        <div
          className="hr-emp-modal-overlay"
          onClick={closeResetPasswordModal}
        >
          <div
            className="hr-emp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {resetPasswordResult ? (
              <>
                <h3 className="hr-emp-modal-title">تم إعادة تعيين كلمة المرور</h3>
                <p className="hr-emp-modal-text">
                  سلّم البيانات التالية للموظف{" "}
                  <strong>{resetPasswordResult.name}</strong> (
                  {resetPasswordResult.code}):
                </p>
                <div className="hr-emp-reset-credentials">
                  <div>
                    كود الموظف: <strong>{resetPasswordResult.code}</strong>
                  </div>
                  <div>
                    كلمة المرور الجديدة:{" "}
                    <strong dir="ltr">{resetPasswordResult.password}</strong>
                  </div>
                </div>
                <p className="hr-emp-field-hint">
                  عند أول دخول بعد الإعادة سيظهر للموظف شاشة تغيير كلمة المرور
                  — يدخل كلمة المرور الحالية (اللي فوق) ويختار كلمة مرور جديدة.
                </p>
                <div className="hr-emp-modal-buttons">
                  <button
                    type="button"
                    className="hr-emp-modal-btn primary"
                    onClick={closeResetPasswordModal}
                  >
                    تم
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="hr-emp-modal-title">إعادة تعيين كلمة المرور</h3>
                <p className="hr-emp-modal-text">
                  إعادة تعيين كلمة مرور{" "}
                  <strong>{selectedEmployee.name}</strong> (
                  {selectedEmployee.code})
                </p>
                <p className="hr-emp-modal-warning">
                  سيتم إنهاء جلسات الدخول الحالية، ويُطلب من الموظف تغيير كلمة
                  المرور عند الدخول التالي.
                </p>
                <div className="hr-emp-modal-field hr-emp-modal-field-full">
                  <label htmlFor="reset-password-input">
                    كلمة المرور المؤقتة <span className="hr-emp-required">*</span>
                  </label>
                  <input
                    id="reset-password-input"
                    type="text"
                    className="hr-emp-modal-input"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    placeholder={DEFAULT_INITIAL_PASSWORD}
                    disabled={resetPasswordSubmitting}
                    dir="ltr"
                    autoComplete="off"
                  />
                  <span className="hr-emp-field-hint">
                    8 أحرف على الأقل، حرف كبير وصغير ورقم ورمز خاص — الافتراضي:{" "}
                    {DEFAULT_INITIAL_PASSWORD}
                  </span>
                </div>
                <div className="hr-emp-modal-buttons">
                  <button
                    type="button"
                    className="hr-emp-modal-btn secondary"
                    onClick={closeResetPasswordModal}
                    disabled={resetPasswordSubmitting}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    className="hr-emp-modal-btn primary"
                    onClick={confirmResetPassword}
                    disabled={
                      resetPasswordSubmitting ||
                      resetPasswordValue.trim().length < 8
                    }
                  >
                    {resetPasswordSubmitting
                      ? "جاري الإعادة..."
                      : "تأكيد الإعادة"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HrEmployeeManagement;
